import { RubyGemMark } from '../../components/RubyGemMark';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Boxes, AlertTriangle, PackagePlus, X, Check, Search, ScanLine,
  Factory, ClipboardList, Building2, ShieldAlert, Tag as TagIcon, Plus, Save, ChevronDown, Printer,
  Monitor, CreditCard, Package, Cable, Pencil, Minus, CopyPlus, Trash2, ImageOff, ArrowLeft,
} from 'lucide-react';
import ShimmerImage from '../../components/ShimmerImage';
import SearchableDropdown from '../../components/SearchableDropdown';
import { toastService } from '../../services/toastService';
import {
  hardwareApi, money, CATEGORY_LABEL, CATEGORY_CHIP, STATUS_CHIP, UNIT_STATUS_LABEL, UNIT_STATUS_CHIP,
  type DeviceModelRow, type DeviceUnitRow, type DeviceBundleRow,
} from '../../services/hardwareApi';
import { billingApi, type BillingSkuRow, type FinanceAgreementRow, type AgreementRollupLike } from '../../services/billingApi';
import {
  inventoryApi, DAMAGE_STATUS_CHIP, DAMAGE_STATUS_LABEL,
  type VendorRow, type GrnRow, type GrnLineInput, type DamageRow, type DamageStatus,
  type StockSummary, type StockMovementRow, type StockAdjustReason,
} from '../../services/inventoryApi';
import { InventoryPage } from './InventoryChrome';
import DamageWizard from './DamageWizard';
import DeviceQuickView from './DeviceQuickView';
import GrnLineComposer from './GrnLineComposer';
import ReceivedUnitCard from './ReceivedUnitCard';
import DeviceImageBackfill from './DeviceImageBackfill';
import DamageResolveActions, { DamageResolutionNote, type DamageResolveResult } from './DamageResolveActions';
import ProvisioningTemplateEditor from './ProvisioningTemplateEditor';
import FinancingPoliciesSection from './FinancingPoliciesSection';
import { FinancingStatTiles, useCostForRow } from './FinancingBook';
import { printUnitLabel } from '../../utils/unitLabelPrint';
import { RubyLoader } from '../../components/RubyLoader';

/**
 * INVENTORY HUB — mockup screen A. ONE page that replaces `CatalogStockPage`
 * (Stock) and `ProcurementPage` (GRN / Vendors / Damage / Brands), plus two
 * new tabs: Adjustments (the old "check in stock" drawer, now its own home)
 * and Provisioning (screen E's per-model template editor). Header-blended
 * tabs, `?tab=` in the URL, mounted at BOTH `/inventory/catalog` (defaults
 * to Stock) and `/inventory/procurement` (defaults to GRN) — see App.tsx.
 */

const DEPLOYED_STATES = new Set(['shipped', 'delivered', 'deployed']);
const HOLD_STATES = new Set(['on_hold', 'backordered', 'rma', 'returned', 'retired']);
// inventory-hub-unit-lifecycle wave — the two new per-model provisioning
// stops. Bucketed on their own so a unit mid-checklist is counted, not
// silently dropped from the stat row / per-model bucket badges.
const PROVISIONING_STATES = new Set(['provisioning', 'configured']);

// Category filter chips + fallback icons — carried over from the retired
// /inventory/models page (CatalogTab), which this Stock tab now replaces.
const CATEGORY_ORDER = ['pos_terminal', 'payment_device', 'peripheral', 'accessory'] as const;
const CATEGORY_ICON: Record<string, React.FC<any>> = {
  pos_terminal: Monitor,
  payment_device: CreditCard,
  peripheral: Package,
  accessory: Cable,
};

// Every former hub tab is its own SIDE-NAV page now (Layout.tsx's Inventory
// group) — one route per section, no in-page tab bar. `route` is where the
// sidebar points; legacy `/inventory/catalog?tab=X` / `/inventory/procurement`
// mounts redirect through this same table.
export const HUB_SECTIONS = [
  { id: 'stock', label: 'Devices & Financing', route: '/inventory/stock', subtitle: 'Every device model, the units behind it, how it is sold, and every live agreement' },
  { id: 'grn', label: 'Receiving', route: '/inventory/receiving', subtitle: 'GRNs — receive vendor shipments into stock' },
  { id: 'vendors', label: 'Vendors', route: '/inventory/vendors', subtitle: 'Who we buy hardware from' },
  { id: 'adjustments', label: 'Adjustments', route: '/inventory/adjustments', subtitle: 'Manual stock check-in and corrections' },
  { id: 'damage', label: 'Damage', route: '/inventory/damage', subtitle: 'Damage reports and their resolutions' },
  { id: 'provisioning', label: 'Provisioning', route: '/inventory/provisioning', subtitle: 'Per-device checklists, written against the order stage each step is worked at' },
  { id: 'brands', label: 'Brands', route: '/inventory/brands', subtitle: 'Device brands across the catalog' },
] as const;

const sectionFor = (id: string) => HUB_SECTIONS.find((s) => s.id === id) || HUB_SECTIONS[0];

// serialized-vs-quantity-tracking wave — approved labels verbatim
// (bulk-vs-serialized-inventory.html screen 1). `isQty` reads absence as
// 'serialized' so every model created before this field existed keeps its
// exact current behavior.
const isQty = (m: Pick<DeviceModelRow, 'trackingMode'>) => m.trackingMode === 'quantity';
const TRACKING_LABEL: Record<'serialized' | 'quantity', string> = {
  serialized: 'Track Item Individually', quantity: 'Track Total Inventory',
};
const TRACKING_CHIP_LABEL: Record<'serialized' | 'quantity', string> = { serialized: 'Individual', quantity: 'Total' };
const TRACKING_CHIP_CLASS: Record<'serialized' | 'quantity', string> = {
  serialized: 'bg-blue-100 text-blue-700',
  quantity: 'bg-rose-100 text-rose-700',
};
const STOCK_ADJUST_REASONS: { id: StockAdjustReason; label: string }[] = [
  { id: 'recount', label: 'Recount / cycle count' },
  { id: 'damaged', label: 'Damaged / unusable' },
  { id: 'consumed', label: 'Consumed internally' },
  { id: 'found', label: 'Found stock' },
  { id: 'other', label: 'Other (note required)' },
];
const MOVEMENT_KIND_LABEL: Record<string, string> = {
  received: 'Received', adjustment: 'Adjustment', reserved: 'Reserved', released: 'Released',
  packed: 'Packed', damaged: 'Damaged',
};

const genPlaceholderSerial = () => `PENDING-${Date.now().toString(36).toUpperCase()}-${Math.random().toString(36).slice(2, 5).toUpperCase()}`;

const NotBuiltYet: React.FC<{ what: string }> = ({ what }) => (
  <div className="rounded-xl border border-dashed border-amber-300 bg-amber-50 py-12 text-center px-6">
    <ShieldAlert size={26} className="mx-auto mb-3 text-amber-500" />
    <p className="text-sm font-semibold text-amber-900">{what} backend not deployed yet</p>
    <p className="mt-1 text-xs text-amber-700">
      This screen is wired up and ready — it will fill in as soon as hardware-service ships this route.
    </p>
  </div>
);

// ---------------------------------------------------------------------------
// STOCK — screen A's landing tab. Model rows expand into their own
// lazily-paginated unit table (never fetch every unit of a model up front).
// ---------------------------------------------------------------------------

interface ModelUnitsState {
  units: DeviceUnitRow[];
  nextToken?: string;
  loading: boolean;
  loadedOnce: boolean;
  error?: string;
}

interface ModelMovementsState {
  items: StockMovementRow[];
  nextToken?: string;
  loading: boolean;
  loadedOnce: boolean;
  error?: string;
}

/** "22 Aug 2026, 14:10" — matches the mockup's ledger timestamps. */
const movementWhen = (at: number) =>
  new Date(at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

const movementLine = (m: StockMovementRow, modelName: (id: string) => string): string => {
  switch (m.kind) {
    case 'received': return `Received${m.grnId ? ` · ${m.grnId}` : ''}${typeof m.unitCostCents === 'number' ? ` @ ${money(m.unitCostCents)}` : ''}`;
    case 'packed': return `Packed${m.orderId ? ` into ${m.orderId}` : ''}${m.boxNo ? ` · box ${m.boxNo}` : ''}`;
    case 'damaged': return `Damaged${m.reason ? `: ${m.reason}` : ''}`;
    case 'reserved': return `Reserved${m.orderId ? ` for ${m.orderId}` : ''}`;
    case 'released': return `Released${m.orderId ? ` from ${m.orderId}` : ''}`;
    case 'adjustment':
    default:
      return `Adjustment${m.reason ? `: ${m.reason}` : ''}${m.note ? ` — ${m.note}` : ''}`;
  }
};

/**
 * ± Adjust stock — mockup screen 4. One signed movement per submit; the
 * server refuses nothing (a recount can go negative, it just carries a
 * `warning`) except a bad `reason:'other'` (needs a note) or `reason:'damaged'`
 * with a positive delta.
 */
const AdjustStockModal: React.FC<{
  model: DeviceModelRow;
  stock?: StockSummary;
  onClose: () => void;
  onAdjusted: () => void;
}> = ({ model, stock, onClose, onAdjusted }) => {
  const [direction, setDirection] = useState<'add' | 'remove'>('remove');
  const [qty, setQty] = useState('1');
  const [reason, setReason] = useState<StockAdjustReason>('recount');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);

  const qtyNum = Number.parseInt(qty, 10);
  const noteRequired = reason === 'other';
  const canSubmit = Number.isFinite(qtyNum) && qtyNum > 0 && (!noteRequired || note.trim()) && !saving;

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    setError(null);
    setWarning(null);
    try {
      const delta = direction === 'remove' ? -qtyNum : qtyNum;
      const res = await inventoryApi.adjustStock(model.modelId, { delta, reason, note: note.trim() || undefined });
      if (res.warning) {
        toastService.error(res.warning);
      } else {
        toastService.success('Adjustment recorded.');
      }
      onAdjusted();
      onClose();
    } catch (e: any) {
      setError(e?.message || 'Failed to record adjustment');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white text-gray-900 shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between bg-black px-5 py-4 text-white">
          <div>
            <h2 className="text-base font-bold">± Adjust stock</h2>
            <p className="text-[11px] text-gray-400 mt-0.5">{model.modelName}{stock ? ` · on hand ${stock.onHand}` : ''}</p>
          </div>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-stone-500 mb-1 font-mono">Direction &amp; quantity</label>
            <div className="flex gap-2">
              <div className="w-40">
                <SearchableDropdown
                  options={[{ value: 'remove', label: '− Remove' }, { value: 'add', label: '＋ Add' }]}
                  value={direction}
                  onChange={(v) => setDirection((v as 'add' | 'remove') || 'remove')}
                  variant="light"
                />
              </div>
              <input
                type="number" min={1} value={qty} onChange={(e) => setQty(e.target.value)}
                className="flex-1 px-3 py-2.5 border border-gray-300 rounded-lg text-sm font-mono min-h-[44px]"
              />
            </div>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-stone-500 mb-1 font-mono">Reason</label>
            <SearchableDropdown
              options={STOCK_ADJUST_REASONS.map((r) => ({ value: r.id, label: r.label }))}
              value={reason}
              onChange={(v) => setReason((v as StockAdjustReason) || 'recount')}
              variant="light"
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-stone-500 mb-1 font-mono">
              Note {noteRequired && <span className="text-red-600">*</span>}
            </label>
            <input
              value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="shelf damage — crushed box"
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm min-h-[44px]"
            />
          </div>
          {error && <p className="text-xs text-red-700 flex items-center gap-1.5"><AlertTriangle size={13} /> {error}</p>}
          {warning && <p className="text-xs text-amber-700 flex items-center gap-1.5"><AlertTriangle size={13} /> {warning}</p>}
          <button
            onClick={submit}
            disabled={!canSubmit}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-white text-sm font-semibold disabled:opacity-40 min-h-[44px]"
            style={{ background: '#DC143C' }}
          >
            {saving ? <RubyGemMark size={15} drawing color="currentColor" /> : <Save size={15} />} Record adjustment
          </button>
          <p className="text-[10.5px] text-stone-500">"Damaged" also lands in the Damage &amp; loss report.</p>
        </div>
      </div>
    </div>
  );
};

/** Section rule for the merged screen's two stat strips. */
const StripLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="mb-2 mt-4 flex items-center gap-2 font-mono text-[9.5px] font-bold uppercase tracking-[.16em] text-stone-400">
    {children}
    <span className="h-px flex-1 bg-stone-200" />
  </div>
);

const StockTab: React.FC<{
  models: DeviceModelRow[];
  /** modelIds that failed schema validation on the service — surfaced LOUD, never silently dropped. */
  invalidModels: string[];
  allUnits: DeviceUnitRow[];
  skus: BillingSkuRow[];
  loading: boolean;
  error: string | null;
  onReceiveStock: () => void;
  onDamage: (unitId: string, costCents: number | undefined) => void;
  reload: () => void;
  /** unitId -> its OPEN damage rows — ONE `GET /damage?status=open` fetch at the hub level, mapped here. No per-unit damage scan. */
  openDamageByUnit: Map<string, DamageRow[]>;
  /** Navigate to the device editor PAGE — '' for a new device. */
  onEditModel: (modelId: string) => void;
  /** Duplicate-for-edit: open the editor page as a COPY of this model (fresh ID + barcode). */
  onDuplicateModel: (modelId: string) => void;
  /* ---- MERGED-IN FINANCING (2026-08-30) ---------------------------------
     Stock and Financing are one screen. These carry the money half: the full
     offer catalog (active AND retired — see the shell's `allSkus`), the
     agreement book, and the joins the policy cards need for device names. */
  allSkus: BillingSkuRow[];
  bundles: DeviceBundleRow[];
  agreements: FinanceAgreementRow[];
  financingGeneratedAt: number | null;
  financingError: string | null;
  /** Whether `agreements` finished paging to exhaustion — gates SKU delete-refusal in `FinancingPoliciesSection`. See Ruling 12. */
  agreementsComplete: boolean;
  /** The Devices screen's own page (up to 20 rows, active+ending) — the FIVE rows that need attention are ranked out of these, never the whole book. */
  financingAttention: FinanceAgreementRow[];
  /** Whole-book totals for the tiles, even though only the attention page is loaded. Absent on the pre-ledger fallback response. */
  financingAttentionTotals: AgreementRollupLike | undefined;
  /** Whether the attention-page fetch above failed. When true, the tiles/rows must NOT render as zero — that would state a money figure the fetch never confirmed. */
  financingAttentionFailed: boolean;
}> = ({
  models, invalidModels, allUnits, skus, loading, error, onReceiveStock, onDamage, reload,
  openDamageByUnit, onEditModel, onDuplicateModel,
  allSkus, bundles, agreements, financingGeneratedAt, financingError, agreementsComplete,
  financingAttention, financingAttentionTotals, financingAttentionFailed,
}) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState<string | null>(null);
  const [category, setCategory] = useState<string>('');
  const [modelUnits, setModelUnits] = useState<Record<string, ModelUnitsState>>({});
  const [lookupCode, setLookupCode] = useState('');
  const [lookingUp, setLookingUp] = useState(false);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [damageBadgeUnitId, setDamageBadgeUnitId] = useState<string | null>(null);
  // Row click opens the device QUICK VIEW popup (info + jump links); the
  // chevron button is what expands the inline units/ledger panel.
  const [quickViewId, setQuickViewId] = useState<string | null>(null);
  /** The draft awaiting a delete confirmation. Never a native confirm(). */
  const [deletingDraft, setDeletingDraft] = useState<DeviceModelRow | null>(null);
  const [deletingDraftBusy, setDeletingDraftBusy] = useState(false);
  // serialized-vs-quantity-tracking wave — quantity-mode models' derived
  // on-hand/reserved (batch call), their movement ledger (per-model, lazy —
  // same pattern as modelUnits above), and the ± Adjust target.
  const [stockByModel, setStockByModel] = useState<Record<string, StockSummary>>({});
  const [modelMovements, setModelMovements] = useState<Record<string, ModelMovementsState>>({});
  const [adjustTarget, setAdjustTarget] = useState<DeviceModelRow | null>(null);
  /** The SAME landed-cost join the book uses, so the totals cannot disagree. */
  const costForRow = useCostForRow(allSkus, models, bundles);

  /**
   * The five rows worth a devices screen's space, picked from the 20 largest
   * balances (the server orders its default active+ending page by remaining
   * balance): unpaid money first, then agreements about to end, then
   * whatever is owed most. This is a heuristic, not a guarantee — a
   * `past_due` agreement with a small balance can still fall outside the 20
   * and never be ranked at all. "Most urgent of the 20 largest balances",
   * not "the 5 most urgent agreements".
   */
  const attentionRows = useMemo(() => {
    const rank = (r: FinanceAgreementRow) =>
      (r.subscriptionStatus === 'past_due' || r.subscriptionStatus === 'unpaid') ? 0
      : r.cancelAtPeriodEnd ? 1 : 2;
    return [...financingAttention].sort((a, b) => rank(a) - rank(b)).slice(0, 5);
  }, [financingAttention]);

  const quantityModelIds = useMemo(
    () => models.filter((m) => isQty(m)).map((m) => m.modelId),
    [models],
  );

  // ONE batch call per page of models — never per-row. Chunked at 50 (the
  // server's own cap on `GET /lifecycle/stock?modelIds=`).
  useEffect(() => {
    if (quantityModelIds.length === 0) return;
    let cancelled = false;
    (async () => {
      const merged: Record<string, StockSummary> = {};
      for (let i = 0; i < quantityModelIds.length; i += 50) {
        const chunk = quantityModelIds.slice(i, i + 50);
        try {
          const res = await inventoryApi.stockSummary(chunk);
          Object.assign(merged, res);
        } catch { /* leave those models' counters unknown rather than failing the whole page */ }
      }
      if (!cancelled) setStockByModel((prev) => ({ ...prev, ...merged }));
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quantityModelIds.join(',')]);

  const fetchModelMovements = useCallback(async (modelId: string, nextToken?: string) => {
    setModelMovements((prev) => ({
      ...prev,
      [modelId]: { items: prev[modelId]?.items || [], loadedOnce: !!prev[modelId]?.loadedOnce, loading: true, error: undefined },
    }));
    try {
      const res = await inventoryApi.stockMovements(modelId, { limit: 15, nextToken });
      setModelMovements((prev) => ({
        ...prev,
        [modelId]: {
          items: nextToken ? [...(prev[modelId]?.items || []), ...(res.items || [])] : (res.items || []),
          nextToken: res.nextToken,
          loading: false,
          loadedOnce: true,
        },
      }));
    } catch (e: any) {
      setModelMovements((prev) => ({
        ...prev,
        [modelId]: { items: prev[modelId]?.items || [], loading: false, loadedOnce: true, error: e?.message || 'Failed to load movements for this model' },
      }));
    }
  }, []);

  const refreshOneModelStock = useCallback(async (modelId: string) => {
    try {
      const res = await inventoryApi.stockSummary([modelId]);
      setStockByModel((prev) => ({ ...prev, ...res }));
    } catch { /* the row just keeps its last-known counters */ }
  }, []);

  const unitsByModel = useMemo(() => {
    const map = new Map<string, DeviceUnitRow[]>();
    for (const u of allUnits) {
      if (!map.has(u.modelId)) map.set(u.modelId, []);
      map.get(u.modelId)!.push(u);
    }
    return map;
  }, [allUnits]);

  const stats = useMemo(() => {
    let inStock = 0, provisioning = 0, deployed = 0, hold = 0;
    for (const u of allUnits) {
      if (u.state === 'in_stock') inStock++;
      else if (PROVISIONING_STATES.has(u.state)) provisioning++;
      else if (DEPLOYED_STATES.has(u.state)) deployed++;
      else if (HOLD_STATES.has(u.state)) hold++;
    }
    return { owned: allUnits.length, inStock, provisioning, deployed, hold };
  }, [allUnits]);

  /**
   * DRAFTS ARE NOT STOCK.
   *
   * A draft is something half-described that somebody stopped part-way through
   * — it has no settled kind, may have no cost, and cannot be sold. Listing it
   * in the same grid as real equipment makes every count on this page a lie:
   * "12 devices" is not 12 things you can put in a bundle if three of them are
   * notes-to-self. So the grid holds PUBLISHED equipment only, the category
   * counts follow it, and drafts get their own area with the two actions that
   * apply to them — finish it, or throw it away.
   */
  const isDraftModel = (m: DeviceModelRow) => m.lifecycle?.status === 'draft';
  const draftModels = useMemo(() => models.filter(isDraftModel), [models]);
  const publishedModels = useMemo(() => models.filter((m) => !isDraftModel(m)), [models]);

  const categoryCounts = useMemo(() => {
    const c: Record<string, number> = {};
    for (const m of publishedModels) c[m.category] = (c[m.category] || 0) + 1;
    return c;
  }, [publishedModels]);

  const visibleModels = useMemo(
    () => (category ? publishedModels.filter((m) => m.category === category) : publishedModels),
    [publishedModels, category],
  );

  /**
   * One device's offers — ACTIVE AND RETIRED, unlike `skus`. The policies
   * cards must show a retired offer so it can be re-offered or deleted;
   * filtering it out here is how it becomes unreachable.
   */
  const offersForModel = useCallback(
    (modelId: string) => allSkus.filter((sk) => sk.modelId === modelId),
    [allSkus],
  );

  const skuModeSetForModel = (modelId: string): Set<string> => {
    const modeSet = new Set<string>();
    for (const s of skus) if (s.modelId === modelId) modeSet.add(s.saleMode || 'outright');
    return modeSet;
  };

  const fetchModelPage = useCallback(async (modelId: string, nextToken?: string) => {
    setModelUnits((prev) => ({
      ...prev,
      [modelId]: { units: prev[modelId]?.units || [], loadedOnce: !!prev[modelId]?.loadedOnce, loading: true, error: undefined },
    }));
    try {
      const res = await hardwareApi.listUnits({ modelId, limit: 25, nextToken });
      setModelUnits((prev) => ({
        ...prev,
        [modelId]: {
          units: nextToken ? [...(prev[modelId]?.units || []), ...(res.items || [])] : (res.items || []),
          nextToken: res.nextToken,
          loading: false,
          loadedOnce: true,
        },
      }));
    } catch (e: any) {
      setModelUnits((prev) => ({
        ...prev,
        [modelId]: { units: prev[modelId]?.units || [], loading: false, loadedOnce: true, error: e?.message || 'Failed to load units for this model' },
      }));
    }
  }, []);

  const toggleExpand = (modelId: string, quantity: boolean) => {
    if (expanded === modelId) { setExpanded(null); return; }
    setExpanded(modelId);
    if (quantity) {
      if (!modelMovements[modelId]?.loadedOnce) fetchModelMovements(modelId);
    } else if (!modelUnits[modelId]?.loadedOnce) {
      fetchModelPage(modelId);
    }
  };

  const runLookup = async () => {
    const code = lookupCode.trim();
    if (!code) return;
    setLookingUp(true);
    setLookupError(null);
    try {
      const res = await hardwareApi.lookupUnitByCode(code);
      if (res.unit) {
        navigate(`/inventory/units/${encodeURIComponent(res.unit.unitId)}`);
      } else if (res.model) {
        // A shared-barcode hit on a quantity model — expand its movements
        // right here instead of a unit page (there is no unit).
        setCategory('');
        toggleExpand(res.model.modelId, true);
        setLookupCode('');
        requestAnimationFrame(() => {
          document.getElementById(`stock-row-${res.model!.modelId}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
      } else {
        setLookupError(`No unit found for "${code}" — check the tracking ID or serial and try again.`);
      }
    } catch (e: any) {
      const msg = String(e?.message || '');
      setLookupError(msg.includes('404') ? `No unit found for "${code}" — check the tracking ID or serial and try again.` : (msg || 'Lookup failed'));
    } finally {
      setLookingUp(false);
    }
  };

  const [printingUnitId, setPrintingUnitId] = useState<string | null>(null);
  const printLabel = async (unitId: string) => {
    setPrintingUnitId(unitId);
    try { await printUnitLabel(unitId); } catch { /* toast not needed — print dialog itself is the feedback */ }
    finally { setPrintingUnitId(null); }
  };

  if (loading) {
    return <RubyLoader variant="page" />;
  }

  return (
    <div>
      {error && (
        <div className="mb-4 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-3">
          <AlertTriangle size={16} className="mt-0.5 shrink-0 text-red-600" />
          <div className="text-sm text-red-800">{error}</div>
        </div>
      )}

      {/* Malformed rows must be LOUD. Silently dropping them is how a device
          quietly vanishes from the catalog and nobody notices for months. */}
      {invalidModels.length > 0 && (
        <div className="mb-4 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3">
          <AlertTriangle size={16} className="mt-0.5 shrink-0 text-amber-600" />
          <div className="text-sm text-amber-900">
            <strong>{invalidModels.length} stored device{invalidModels.length === 1 ? '' : 's'} failed validation</strong> and
            {invalidModels.length === 1 ? ' is' : ' are'} hidden: <span className="font-mono text-xs">{invalidModels.join(', ')}</span>
          </div>
        </div>
      )}

      {/* Barcode / tracking-ID lookup */}
      <div className="mb-5 flex items-center gap-2 rounded-xl border border-stone-200 bg-white p-3">
        <ScanLine size={18} className="text-stone-400 shrink-0" />
        <input
          value={lookupCode}
          onChange={(e) => { setLookupCode(e.target.value); setLookupError(null); }}
          onKeyDown={(e) => { if (e.key === 'Enter') runLookup(); }}
          placeholder="Scan or type a tracking ID / serial number…"
          className="flex-1 text-sm font-mono outline-none min-h-[36px]"
        />
        <button
          onClick={runLookup}
          disabled={lookingUp || !lookupCode.trim()}
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-stone-300 text-stone-700 text-xs font-semibold disabled:opacity-40 min-h-[36px]"
        >
          {lookingUp ? <RubyGemMark size={13} drawing color="currentColor" /> : <Search size={13} />} Look up
        </button>
      </div>
      {lookupError && <p className="-mt-3 mb-4 text-xs text-red-600">{lookupError}</p>}

      <StripLabel>Stock</StripLabel>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div className="rounded-xl border border-stone-200 bg-white p-4">
          <div className="text-2xl font-light text-stone-900">{stats.owned}</div>
          <div className="text-[10px] uppercase tracking-wider text-stone-500 mt-1 font-mono">Units owned</div>
        </div>
        <div className="rounded-xl border border-stone-200 bg-white p-4">
          <div className="text-2xl font-light text-stone-900">{stats.inStock}</div>
          <div className="text-[10px] uppercase tracking-wider text-stone-500 mt-1 font-mono">In stock</div>
        </div>
        <div className="rounded-xl border border-stone-200 bg-white p-4">
          <div className="text-2xl font-light text-rose-600">{stats.provisioning}</div>
          <div className="text-[10px] uppercase tracking-wider text-stone-500 mt-1 font-mono">Provisioning</div>
        </div>
        <div className="rounded-xl border border-stone-200 bg-white p-4">
          <div className="text-2xl font-light text-stone-900">{stats.deployed}</div>
          <div className="text-[10px] uppercase tracking-wider text-stone-500 mt-1 font-mono">Deployed</div>
        </div>
        <div className="rounded-xl border border-stone-200 bg-white p-4">
          <div className="text-2xl font-light text-amber-600">{stats.hold}</div>
          <div className="text-[10px] uppercase tracking-wider text-stone-500 mt-1 font-mono">RMA / hold</div>
        </div>
      </div>

      {/* THE MONEY HALF (2026-08-30) — the five totals that used to head the
          standalone Financing page, on the same screen as the stock they are
          about. */}
      <StripLabel>Financing</StripLabel>
      {financingError ? (
        <div className="mb-5 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
          <AlertTriangle size={15} className="mt-0.5 shrink-0" />
          Could not load the agreement book ({financingError}). Stock above is unaffected.
        </div>
      ) : (
        <div className="mb-5">
          <FinancingStatTiles rows={agreements} costForRow={costForRow} />
        </div>
      )}

      {/* Category filters + new device — the /inventory/models look, now living here */}
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setCategory('')}
            className={`rounded-lg px-3 py-2 text-xs font-semibold transition ${
              category === '' ? 'text-white' : 'border border-stone-300 bg-white text-stone-700 hover:bg-stone-50'
            }`}
            style={category === '' ? { background: '#DC143C' } : undefined}
          >
            All · {publishedModels.length}
          </button>
          {CATEGORY_ORDER.map((c) => {
            const Icon = CATEGORY_ICON[c];
            return (
              <button
                key={c}
                onClick={() => setCategory(c)}
                className={`flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold transition ${
                  category === c ? 'text-white' : 'border border-stone-300 bg-white text-stone-700 hover:bg-stone-50'
                }`}
                style={category === c ? { background: '#DC143C' } : undefined}
              >
                <Icon size={13} />
                {CATEGORY_LABEL[c]} · {categoryCounts[c] || 0}
              </button>
            );
          })}
        </div>
        <button
          onClick={() => onEditModel('')}
          className="flex items-center gap-1.5 rounded-lg px-4 py-2 text-xs font-semibold text-white min-h-[38px]"
          style={{ background: '#DC143C' }}
        >
          ＋ New Equipment
        </button>
      </div>

      {/* UNFINISHED ITEMS, kept out of the grid but never out of sight. Hiding
          them entirely is how a half-entered device is silently re-created a
          week later by somebody who could not find the first one. */}
      {draftModels.length > 0 && (
        <div className="mb-5 rounded-xl border border-amber-200 bg-amber-50/60 p-4">
          <div className="mb-2.5 flex items-center gap-2">
            <Pencil size={14} className="text-amber-700" />
            <h3 className="text-[12px] font-semibold uppercase tracking-wider text-amber-800">
              Drafts · {draftModels.length}
            </h3>
            <span className="text-[11.5px] text-amber-700/80">
              Not in stock, not sellable, and never part of a bundle until published.
            </span>
          </div>
          <div className="grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-3">
            {draftModels.map((m) => (
                <div
                  key={m.modelId}
                  className="flex items-center gap-2.5 rounded-lg border border-amber-200 bg-white px-3 py-2.5"
                >
                  {/* THE PICTURE AND THE CATEGORY, same as a published row.
                      A draft was showing its raw Device ID, which is the one
                      thing on the card nobody recognises — the photo and "Card
                      reader" are how an operator knows which half-finished
                      device this is. */}
                  <ShimmerImage
                    src={m.media?.heroImageUrl}
                    alt={m.modelName}
                    width={36}
                    height={36}
                    objectFit="contain"
                    className="shrink-0 rounded-lg"
                    fallbackBgColor="bg-amber-100"
                    fallbackIcon={React.createElement(CATEGORY_ICON[m.category] || Package, { size: 16, className: 'text-amber-600' })}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[13px] font-semibold text-stone-800">
                      {m.modelName || <span className="text-stone-400">Untitled</span>}
                    </div>
                    <div className="truncate text-[10.5px] text-stone-500">
                      {CATEGORY_LABEL[m.category] || m.category}
                    </div>
                  </div>
                  <button
                    onClick={() => onEditModel(m.modelId)}
                    className="shrink-0 rounded-md border border-stone-300 px-2.5 py-1.5 text-[11.5px] font-semibold text-stone-700 hover:bg-stone-50"
                  >
                    Continue
                  </button>
                  <button
                    onClick={() => setDeletingDraft(m)}
                    title="Delete this draft"
                    className="shrink-0 rounded-md border border-stone-300 p-1.5 text-stone-400 hover:border-red-300 hover:text-red-600"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
            ))}
          </div>
        </div>
      )}

      {publishedModels.length === 0 && draftModels.length === 0 ? (
        <div className="rounded-xl border border-dashed border-stone-300 bg-white py-16 text-center">
          <Boxes size={28} className="mx-auto mb-3 text-stone-300" />
          <p className="text-sm font-medium text-stone-700">No devices in the catalog yet</p>
          <p className="mt-1 text-xs text-stone-500">Add a device to record its cost, specs and start tracking stock.</p>
        </div>
      ) : visibleModels.length === 0 ? (
        <div className="rounded-xl border border-dashed border-stone-300 bg-white py-16 text-center">
          <Package size={28} className="mx-auto mb-3 text-stone-300" />
          <p className="text-sm font-medium text-stone-700">No devices in this category</p>
          <p className="mt-1 text-xs text-stone-500">Try another category.</p>
        </div>
      ) : (
        // Two-level card rows — everything visible at once, no sideways scroll.
        // Level 1: identity + chips + actions. Level 2: the numbers strip.
        <div className="overflow-hidden rounded-xl border border-stone-200 bg-white">
          <div className="divide-y divide-stone-100">
            {visibleModels.map((m) => {
            const quantity = isQty(m);
            const modelUnitsAll = unitsByModel.get(m.modelId) || [];
            const qStock = stockByModel[m.modelId];
            const inStock = quantity ? (qStock?.onHand ?? 0) : modelUnitsAll.filter((u) => u.state === 'in_stock').length;
            const allocated = quantity ? (qStock?.reserved ?? 0) : modelUnitsAll.filter((u) => u.state === 'allocated').length;
            const provisioning = modelUnitsAll.filter((u) => PROVISIONING_STATES.has(u.state)).length;
            const deployed = modelUnitsAll.filter((u) => DEPLOYED_STATES.has(u.state)).length;
            const rma = modelUnitsAll.filter((u) => HOLD_STATES.has(u.state)).length;
            const modes = skuModeSetForModel(m.modelId);
            const financeModes = ['installment', 'lease', 'rental'].some((x) => modes.has(x));
            const offerCount = skus.filter((sk) => sk.modelId === m.modelId).length;
            const reorderPoint = (m as any).reorderPoint as number | undefined;
            const low = typeof reorderPoint === 'number' && (!quantity || qStock) && inStock < reorderPoint;
            const valueAtCost = quantity && qStock ? qStock.onHand * (m.landedCostCents || 0) : undefined;
            const isOpen = expanded === m.modelId;
            const state = modelUnits[m.modelId];
            const mState = modelMovements[m.modelId];

            /*
              SELLABILITY, NOT "HAS A POLICY" (2026-08-30).

              The old chip shouted "⚠ No financing policy" at every cable and
              label in the catalog, which is not a problem — a consumable is
              not sold to tenants and never will be. A device now declares
              whether it is sold at all (`sellable`), and the warning fires on
              the one state that IS a mistake: marked sellable, no terms set.
            */
            /*
              ABSENT MEANS "NEVER ASKED", and is resolved rather than assumed.
              Every device in the catalog predates this field. Defaulting them
              all to sellable would reproduce the exact warning this replaces
              on every cable; defaulting them all to not-sellable would hide
              real offers. So an unanswered device is sellable IF it already
              has offers, and stock-only otherwise — where one click on
              "Make sellable…" answers it for good.
            */
            const sellable = m.sellable === true || (m.sellable == null && offerCount > 0);
            let policyChip: { label: string; cls: string };
            if (!sellable) {
              policyChip = { label: 'Stock only — not sold', cls: 'bg-stone-100 text-stone-600 border-stone-200' };
            } else if (offerCount === 0) {
              policyChip = { label: '⚠ Sellable, no terms', cls: 'bg-amber-100 text-amber-700 border-amber-200' };
            } else if (financeModes) {
              policyChip = { label: `Sellable · ${offerCount} offer${offerCount === 1 ? '' : 's'}`, cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' };
            } else {
              policyChip = { label: `Sale only · ${offerCount} offer${offerCount === 1 ? '' : 's'}`, cls: 'bg-blue-100 text-blue-700 border-blue-200' };
            }

            return (
              <React.Fragment key={m.modelId}>
                <div
                  id={`stock-row-${m.modelId}`}
                  onClick={() => setQuickViewId(m.modelId)}
                  className="cursor-pointer px-4 py-3 transition hover:bg-stone-50"
                >
                  {/* Level 1 — identity, chips, actions */}
                  <div className="flex flex-wrap items-center gap-3">
                    <ShimmerImage
                      src={m.media?.heroImageUrl}
                      alt={m.modelName}
                      width={44}
                      height={44}
                      objectFit="contain"
                      className="rounded-lg shrink-0"
                      fallbackBgColor="bg-stone-900"
                      fallbackIcon={React.createElement(CATEGORY_ICON[m.category] || Package, { size: 18, className: 'text-stone-400' })}
                      variant="dark"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span className="text-sm font-semibold text-stone-900">{m.modelName}</span>
                        <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold whitespace-nowrap ${CATEGORY_CHIP[m.category] || 'bg-stone-100 text-stone-600'}`}>
                          {CATEGORY_LABEL[m.category] || m.category}
                        </span>
                        <span
                          title={TRACKING_LABEL[quantity ? 'quantity' : 'serialized']}
                          className={`rounded-full px-2 py-0.5 text-[10px] font-semibold whitespace-nowrap ${TRACKING_CHIP_CLASS[quantity ? 'quantity' : 'serialized']}`}
                        >
                          {TRACKING_CHIP_LABEL[quantity ? 'quantity' : 'serialized']}
                        </span>
                        <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${STATUS_CHIP[m.lifecycle?.status] || 'bg-stone-100 text-stone-600'}`}>
                          {(m.lifecycle?.status || '').toUpperCase()}
                        </span>
                        <span className={`rounded-full border px-2 py-0.5 text-[9px] font-semibold whitespace-nowrap ${policyChip.cls}`}>
                          {policyChip.label}
                        </span>
                      </div>
                      <div className="mt-0.5 truncate text-[11px] text-stone-500">
                        {[m.manufacturer, m.specs?.androidVersion && `Android ${m.specs.androidVersion}`,
                          `${skus.filter((sk) => sk.modelId === m.modelId).length} offer(s)`].filter(Boolean).join(' · ')}
                      </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => onEditModel(m.modelId)}
                        title="Edit device model — cost, specs, media"
                        className="inline-flex items-center gap-1 rounded-lg border border-stone-300 bg-white px-2.5 py-1.5 text-[11px] font-semibold text-stone-700 hover:bg-stone-50"
                      >
                        <Pencil size={12} /> Edit
                      </button>
                      <button
                        onClick={() => onDuplicateModel(m.modelId)}
                        title="Duplicate — copy this device as a new variation (fresh ID + barcode, everything else carried over)"
                        className="inline-flex items-center gap-1 rounded-lg border border-stone-300 bg-white px-2.5 py-1.5 text-[11px] font-semibold text-stone-700 hover:bg-stone-50"
                      >
                        <CopyPlus size={12} /> Duplicate
                      </button>
                      {/* MERGED SCREEN (2026-08-30): selling terms are reachable
                          from the device itself — no trip to a second page. */}
                      <button
                        onClick={() => navigate(offerCount > 0
                          ? `/inventory/financing/edit?edit=model:${encodeURIComponent(m.modelId)}`
                          : `/inventory/financing/new?target=model:${encodeURIComponent(m.modelId)}`)}
                        title={sellable
                          ? 'Selling & financing terms for this device'
                          : 'This device is stock-only — set terms to start selling it'}
                        className="inline-flex items-center gap-1 rounded-lg border px-2.5 py-1.5 text-[11px] font-semibold hover:bg-rose-50"
                        style={{ borderColor: '#fbcfe0', color: '#be123c' }}
                      >
                        <CreditCard size={12} /> {sellable ? 'Financing' : 'Make sellable…'}
                      </button>
                      {quantity && (
                        <button
                          onClick={() => setAdjustTarget(m)}
                          title="± Adjust stock"
                          className="inline-flex items-center gap-1 rounded-lg border border-stone-300 bg-white px-2.5 py-1.5 text-[11px] font-semibold text-stone-700 hover:bg-stone-50"
                        >
                          ± Adjust
                        </button>
                      )}
                      <button
                        onClick={() => toggleExpand(m.modelId, quantity)}
                        title={quantity ? 'Show the stock ledger' : 'Show this model’s units'}
                        className="rounded-lg border border-stone-300 bg-white p-1.5 text-stone-500 hover:bg-stone-50"
                      >
                        <ChevronDown size={15} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                      </button>
                    </div>
                  </div>
                  {/* Level 2 — the numbers, labelled, roomy, wraps instead of scrolling */}
                  <div className="mt-2.5 flex flex-wrap items-center gap-x-8 gap-y-1.5 pl-[56px]">
                    <span className="text-xs"><b className="font-mono text-stone-900">{money(m.landedCostCents)}</b> <span className="text-stone-400">landed</span></span>
                    <span className="text-xs">
                      <b className={`font-mono ${low ? 'text-amber-600' : inStock > 0 ? 'text-stone-900' : 'text-stone-400'}`}>
                        {quantity && !qStock ? <span className="inline-flex"><RubyGemMark size={11} drawing /></span> : inStock}
                      </b>{' '}
                      <span className="text-stone-400">in stock</span>
                      {low && <span className="ml-1 text-[10px] font-semibold text-amber-600">below reorder ({reorderPoint})</span>}
                      {typeof valueAtCost === 'number' && <span className="ml-1 text-[10px] text-stone-400">· {money(valueAtCost)} value</span>}
                    </span>
                    <span className="text-xs"><b className={`font-mono ${allocated > 0 ? 'text-stone-900' : 'text-stone-400'}`}>{allocated}</b> <span className="text-stone-400">{quantity ? 'reserved' : 'allocated'}</span></span>
                    {!quantity && (
                      <>
                        <span className="text-xs"><b className={`font-mono ${provisioning > 0 ? 'text-rose-700' : 'text-stone-400'}`}>{provisioning}</b> <span className="text-stone-400">provisioning</span></span>
                        <span className="text-xs"><b className={`font-mono ${deployed > 0 ? 'text-stone-900' : 'text-stone-400'}`}>{deployed}</b> <span className="text-stone-400">deployed</span></span>
                        <span className="text-xs"><b className={`font-mono ${rma > 0 ? 'text-red-600' : 'text-stone-400'}`}>{rma}</b> <span className="text-stone-400">RMA / hold</span></span>
                      </>
                    )}
                  </div>
                </div>

                {/* THE FINANCING LANE (2026-08-30) — this device's own offers,
                    inside its own row. The policies section that used to be a
                    single global list on the Financing page is rendered here
                    per device, with every lifecycle action unchanged. It leads
                    the expanded panel because "how is this sold?" is the
                    question the merged screen exists to answer next to "how
                    many have we got?". */}
                {isOpen && (
                  <div className="border-t border-stone-100 bg-white px-4 py-3">
                    <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                      <h4 className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-stone-500">
                        <CreditCard size={12} /> Selling &amp; financing
                      </h4>
                      <button
                        onClick={() => navigate(offersForModel(m.modelId).length > 0
                          ? `/inventory/financing/edit?edit=model:${encodeURIComponent(m.modelId)}`
                          : `/inventory/financing/new?target=model:${encodeURIComponent(m.modelId)}`)}
                        className="rounded-lg px-3 py-1.5 text-[11px] font-semibold text-white"
                        style={{ background: '#DC143C' }}
                      >
                        {offersForModel(m.modelId).length > 0 ? 'Edit terms in wizard' : '+ Add selling terms'}
                      </button>
                    </div>
                    <FinancingPoliciesSection
                      variant="inline"
                      skus={offersForModel(m.modelId)}
                      agreements={agreements}
                      agreementsComplete={agreementsComplete}
                      models={models}
                      bundles={bundles}
                      onChanged={reload}
                      emptyHint="No selling terms for this device yet — it is stock only."
                    />
                  </div>
                )}

                {isOpen && quantity && (
                  <div className="border-t border-stone-100 bg-stone-50 px-4 py-3">
                      {mState?.error && (
                        <div className="mb-2 text-xs text-red-700 flex items-center gap-1.5"><AlertTriangle size={13} /> {mState.error}</div>
                      )}
                      {!mState || (mState.loading && mState.items.length === 0) ? (
                        <RubyLoader variant="section" />
                      ) : mState.items.length === 0 ? (
                        <p className="text-xs text-stone-500 py-4 text-center">No stock movements recorded for this item yet.</p>
                      ) : (
                        <div>
                          <ul className="space-y-0">
                            {mState.items.map((mv, i) => (
                              <li key={`${mv.at}-${i}`} className="flex items-center gap-2.5 py-1.5 border-t border-dashed border-stone-200 first:border-t-0 text-[11.5px]">
                                <span className={`font-mono font-bold ${mv.qty > 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                                  {mv.qty > 0 ? `+${mv.qty}` : mv.qty}
                                </span>
                                <span className="text-stone-700">{movementLine(mv, () => m.modelName)}</span>
                                <span className="ml-auto text-[9.5px] font-mono text-stone-400 whitespace-nowrap">
                                  {mv.actor ? `${mv.actor} · ` : ''}{movementWhen(mv.at)}
                                </span>
                              </li>
                            ))}
                          </ul>
                          {mState.nextToken && (
                            <div className="pt-2 text-center">
                              <button
                                onClick={() => fetchModelMovements(m.modelId, mState.nextToken)}
                                disabled={mState.loading}
                                className="px-3 py-1.5 rounded-lg border border-stone-300 bg-white text-stone-700 text-[11px] font-semibold disabled:opacity-50"
                              >
                                {mState.loading ? <span className="inline-flex"><RubyGemMark size={12} drawing color="currentColor" /></span> : 'Load older'}
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                      <div className="mt-3 flex items-center justify-between">
                        <p className="text-[10px] text-stone-500">On-hand = the ledger added up — it can't be edited directly.</p>
                        <button
                          onClick={() => setAdjustTarget(m)}
                          className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-white text-[11px] font-semibold"
                          style={{ background: '#DC143C' }}
                        >
                          ± Adjust stock
                        </button>
                      </div>
                  </div>
                )}

                {isOpen && !quantity && (
                  <div className="border-t border-stone-100 bg-stone-50 px-4 py-3">
                    {state?.error && (
                      <div className="mb-2 text-xs text-red-700 flex items-center gap-1.5"><AlertTriangle size={13} /> {state.error}</div>
                    )}
                    {!state || (state.loading && state.units.length === 0) ? (
                      <RubyLoader variant="section" />
                    ) : state.units.length === 0 ? (
                      <p className="text-xs text-stone-500 py-4 text-center">No units of this model yet.</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-[11px]">
                          <thead>
                            <tr className="text-left text-[9px] uppercase tracking-wider text-stone-500 border-b border-stone-200">
                              <th className="py-1.5 pr-3 font-semibold">Tracking</th>
                              <th className="py-1.5 pr-3 font-semibold">Serial</th>
                              <th className="py-1.5 pr-3 font-semibold">MAC</th>
                              <th className="py-1.5 pr-3 font-semibold">State</th>
                              <th className="py-1.5 pr-3 font-semibold">Provisioning</th>
                              <th className="py-1.5 pr-3 font-semibold">Order</th>
                              <th className="py-1.5 pr-3 font-semibold text-right">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {state.units.map((u) => {
                              const steps = u.provisioning?.steps || [];
                              const required = steps.filter((s) => s.required).length;
                              const done = steps.filter((s) => s.required && s.done).length;
                              const pct = required > 0 ? Math.round((done / required) * 100) : (steps.length > 0 ? 100 : 0);
                              const openDamage = openDamageByUnit.get(u.unitId) || [];
                              const badgeOpen = damageBadgeUnitId === u.unitId;
                              return (
                                <React.Fragment key={u.unitId}>
                                  <tr className="border-b border-stone-200/70 font-mono">
                                    <td className="py-1.5 pr-3 text-red-500">
                                      {u.trackingId || u.unitId}
                                      {openDamage.length > 0 && (
                                        <button
                                          onClick={() => setDamageBadgeUnitId(badgeOpen ? null : u.unitId)}
                                          title={`${openDamage.length} open damage report${openDamage.length === 1 ? '' : 's'}`}
                                          className="ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full bg-amber-100 border border-amber-300 text-amber-700 text-[9px] font-bold align-middle"
                                        >
                                          ⚠
                                        </button>
                                      )}
                                    </td>
                                    <td className="py-1.5 pr-3 text-stone-600">{u.serialNumber || '—'}</td>
                                    <td className="py-1.5 pr-3 text-stone-500">{u.mac || '—'}</td>
                                    <td className="py-1.5 pr-3">
                                      <span className={`inline-block text-[9.5px] font-semibold rounded-full px-2 py-0.5 border-0 ${UNIT_STATUS_CHIP[u.state]}`}>
                                        {UNIT_STATUS_LABEL[u.state]}
                                      </span>
                                    </td>
                                    <td className="py-1.5 pr-3">
                                      {u.provisioning ? (
                                        <span className="inline-flex items-center gap-1.5">
                                          <span className="inline-block w-12 h-1 rounded-full bg-stone-200 overflow-hidden align-middle">
                                            <span className="block h-full bg-emerald-500" style={{ width: `${pct}%` }} />
                                          </span>
                                          {done}/{required || steps.length}
                                        </span>
                                      ) : '—'}
                                    </td>
                                    <td className="py-1.5 pr-3 text-stone-500">
                                      {u.orderId ? (
                                        <button onClick={() => navigate(`/inventory/orders?open=${encodeURIComponent(u.orderId!)}`)} className="text-red-500 hover:underline">{u.orderId}</button>
                                      ) : '—'}
                                    </td>
                                    <td className="py-1.5 pr-3 text-right whitespace-nowrap">
                                      <button onClick={() => navigate(`/inventory/units/${encodeURIComponent(u.unitId)}`)} className="mini-btn">Unit page</button>{' '}
                                      <button onClick={() => printLabel(u.unitId)} disabled={printingUnitId === u.unitId} className="mini-btn">
                                        {printingUnitId === u.unitId ? '…' : 'Label'}
                                      </button>{' '}
                                      <button onClick={() => onDamage(u.unitId, u.costCents ?? m.landedCostCents)} className="mini-btn-danger">⚠ Damage</button>
                                    </td>
                                  </tr>
                                  {badgeOpen && (
                                    <tr className="border-b border-stone-200/70 bg-amber-50/60">
                                      <td colSpan={7} className="py-2.5 px-3">
                                        <div className="space-y-2">
                                          {openDamage.map((d) => (
                                            <div key={d.createdAt} className="flex items-center justify-between gap-3 rounded-lg border border-amber-200 bg-white px-3 py-2 flex-wrap">
                                              <div className="text-[11px] text-stone-700">
                                                <span className="font-semibold">{d.reason}</span>
                                                <span className="text-stone-400"> · {d.stage} · charged to {d.chargedTo}{typeof d.amountCents === 'number' ? ` · $${(d.amountCents / 100).toFixed(2)}` : ''}</span>
                                              </div>
                                              <DamageResolveActions
                                                damage={d}
                                                compact
                                                onResolved={(res) => { setDamageBadgeUnitId(null); reload(); }}
                                              />
                                            </div>
                                          ))}
                                        </div>
                                      </td>
                                    </tr>
                                  )}
                                </React.Fragment>
                              );
                            })}
                          </tbody>
                        </table>
                        {state.nextToken && (
                          <div className="pt-2 text-center">
                            <button
                              onClick={() => fetchModelPage(m.modelId, state.nextToken)}
                              disabled={state.loading}
                              className="px-3 py-1.5 rounded-lg border border-stone-300 bg-white text-stone-700 text-[11px] font-semibold disabled:opacity-50"
                            >
                              {state.loading ? <span className="inline-flex"><RubyGemMark size={12} drawing color="currentColor" /></span> : 'Load more'}
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                    <p className="mt-2 text-[10px] text-stone-500">
                      Checkout of a specific machine happens by allocation (FIFO by default, override per unit).
                    </p>
                  </div>
                )}
              </React.Fragment>
            );
          })}
          </div>
        </div>
      )}

      {/* AGREEMENTS — a SUMMARY, not the book (spec 2026-08-30).
          The full book moved to /inventory/finance-book: rendering every
          agreement here had no paging, no filter and no search, and it grew
          without bound because cancelled rows never left. What stays is what
          a devices screen actually needs — the totals, and the handful of
          rows that need someone to act. Five, per the embedded-list cap. */}
      <div className="mt-8">
        <div className="mb-3 flex items-end justify-between gap-3">
          <div>
            <h2 className="mb-1 text-sm font-bold text-stone-900">Agreements</h2>
            <p className="text-[11px] text-stone-500">
              What the fleet is earning, and the agreements that need attention.
            </p>
          </div>
          <button
            onClick={() => navigate('/inventory/finance-book')}
            className="shrink-0 rounded-lg border border-stone-300 bg-white px-3 py-2 text-[11px] font-semibold text-stone-700 min-h-[44px]"
          >
            View all{financingAttentionTotals ? ` ${financingAttentionTotals.agreements}` : ''} agreements →
          </button>
        </div>

        {financingAttentionFailed ? (
          // A tile stating a money figure is an assertion of fact — do not
          // render $0/0 when the fetch that would back that figure failed.
          // Same tone as the top strip's own failure message.
          <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
            <AlertTriangle size={15} className="mt-0.5 shrink-0" />
            Could not load the agreement summary. Devices above are unaffected.
          </div>
        ) : (
          <>
            <FinancingStatTiles
              rows={financingAttention}
              costForRow={costForRow}
              serverTotals={financingAttentionTotals}
            />
            {attentionRows.length > 0 && (
              <div className="mt-3 divide-y divide-stone-100 rounded-xl border border-stone-200 bg-white">
                {attentionRows.map((r) => (
                  <button
                    key={`${r.subscriptionId}-${r.skuId}`}
                    onClick={() => navigate('/inventory/finance-book')}
                    className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left min-h-[44px]"
                  >
                    <span className="min-w-0 flex-1 truncate text-xs font-medium text-stone-800">
                      {r.tenantName || r.tenantId || '— unattributed —'}
                      <span className="ml-2 font-normal text-stone-500">{r.name}</span>
                    </span>
                    <span className="shrink-0 font-mono text-[11px] text-stone-900">
                      {r.remainingCents === null ? money(r.monthlyCents) + '/mo' : money(r.remainingCents)}
                    </span>
                    <span className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold ${
                      r.subscriptionStatus === 'past_due' || r.subscriptionStatus === 'unpaid'
                        ? 'bg-red-100 text-red-700'
                        : r.cancelAtPeriodEnd ? 'bg-amber-100 text-amber-700'
                        : 'bg-stone-100 text-stone-600'
                    }`}>
                      {r.subscriptionStatus === 'past_due' || r.subscriptionStatus === 'unpaid'
                        ? r.subscriptionStatus
                        : r.cancelAtPeriodEnd ? 'ending' : 'owing most'}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      <p className="mt-3 text-[11px] text-stone-500">
        <strong>Landed cost</strong> = unit cost + inbound shipping + duty — cost lives on the device,
        <strong> price never does</strong>; that stays in the billing catalog (edit a device to see the breakdown).
        Low-stock threshold uses each model's <code className="font-mono">reorderPoint</code>. Tabs carried over: <b>GRN</b>
        (vendor receiving), <b>Vendors</b> (scorecards), <b>Brands</b> (from model.brand). <b>Adjustments</b> = check-in
        (no-PO additions). <b>Damage</b> = the log; every unit row above also has ⚠ → the damage wizard.
      </p>

      <style>{`
        .mini-btn { border:1px solid #e7e5e4; background:#fff; color:#44403c; border-radius:6px; padding:3px 8px; font-size:10px; font-weight:600; cursor:pointer; }
        .mini-btn-danger { border:1px solid #fecaca; background:#fff; color:#b91c1c; border-radius:6px; padding:3px 8px; font-size:10px; font-weight:600; cursor:pointer; }
      `}</style>

      {deletingDraft && (
        <div className="fixed inset-0 z-[9998] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-stone-900/60 backdrop-blur-sm"
            onClick={() => !deletingDraftBusy && setDeletingDraft(null)}
          />
          <div className="relative w-full max-w-md rounded-2xl border border-stone-200 bg-white p-5 shadow-2xl">
            <h3 className="text-base font-bold text-stone-900">Delete this draft?</h3>
            <p className="mt-2 text-sm text-stone-600">
              {deletingDraft.modelName || deletingDraft.modelId}
            </p>
            <p className="mt-2 text-[12px] text-stone-500">
              A draft has never been published, so nothing references it — no stock, no bundle, no
              offer. Deleting it removes it for good.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button
                onClick={() => setDeletingDraft(null)}
                disabled={deletingDraftBusy}
                className="min-h-[44px] rounded-lg border border-stone-300 px-4 text-sm font-medium text-stone-700 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  setDeletingDraftBusy(true);
                  try {
                    await hardwareApi.deleteModel(deletingDraft.modelId);
                    toastService.success('Draft deleted');
                    setDeletingDraft(null);
                    reload();
                  } catch (e: any) {
                    // The service refuses a delete that would orphan something.
                    // Its reason is more useful than anything invented here.
                    toastService.error(e?.message || 'Could not delete the draft');
                  } finally {
                    setDeletingDraftBusy(false);
                  }
                }}
                disabled={deletingDraftBusy}
                className="min-h-[44px] rounded-lg bg-red-600 px-4 text-sm font-semibold text-white disabled:opacity-60"
              >
                {deletingDraftBusy ? 'Deleting…' : 'Delete draft'}
              </button>
            </div>
          </div>
        </div>
      )}

      {quickViewId && (
        <DeviceQuickView
          modelId={quickViewId}
          models={models}
          units={allUnits}
          skus={skus}
          stockByModel={stockByModel}
          onClose={() => setQuickViewId(null)}
          onEdit={(id) => { setQuickViewId(null); onEditModel(id); }}
          onDuplicate={(id) => { setQuickViewId(null); onDuplicateModel(id); }}
          onViewUnits={(id) => {
            setQuickViewId(null);
            const target = models.find((x) => x.modelId === id);
            if (target && expanded !== id) toggleExpand(id, isQty(target));
            setTimeout(() => document.getElementById(`stock-row-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50);
          }}
        />
      )}

      {adjustTarget && (
        <AdjustStockModal
          model={adjustTarget}
          stock={stockByModel[adjustTarget.modelId]}
          onClose={() => setAdjustTarget(null)}
          onAdjusted={() => {
            const id = adjustTarget.modelId;
            refreshOneModelStock(id);
            if (modelMovements[id]?.loadedOnce) fetchModelMovements(id);
            reload();
          }}
        />
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// ADJUSTMENTS — the old "check in stock" drawer, now the tab's main body.
// ---------------------------------------------------------------------------

const AdjustmentsTab: React.FC<{ models: DeviceModelRow[]; reload: () => void }> = ({ models, reload }) => {
  const [modelId, setModelId] = useState('');
  const [qty, setQty] = useState('1');
  const [serials, setSerials] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const modelOptions = useMemo(
    () => models.map((m) => ({ value: m.modelId, label: m.modelName, subtitle: CATEGORY_LABEL[m.category] || m.category })),
    [models],
  );

  const qtyNum = Number.parseInt(qty, 10);
  const parsedSerials = serials.split(/[\n,]/).map((s) => s.trim()).filter(Boolean);

  const submit = async () => {
    if (!modelId) { setError('Pick a device model.'); return; }
    if (!Number.isFinite(qtyNum) || qtyNum < 1) { setError('Quantity must be at least 1.'); return; }
    setSaving(true);
    setError(null);
    try {
      const results = await Promise.allSettled(
        Array.from({ length: qtyNum }, (_, i) =>
          hardwareApi.upsertUnit({ modelId, serialNumber: parsedSerials[i] || genPlaceholderSerial(), state: 'in_stock' }),
        ),
      );
      const failed = results.filter((r) => r.status === 'rejected').length;
      const ok = results.length - failed;
      if (ok > 0) toastService.success(`Checked in ${ok} unit${ok === 1 ? '' : 's'} → in stock.`);
      if (failed > 0) toastService.error(`${failed} unit${failed === 1 ? '' : 's'} failed to check in.`);
      setModelId(''); setQty('1'); setSerials('');
      reload();
    } catch (e: any) {
      setError(e?.message || 'Check-in failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="mb-4 flex items-start gap-2 rounded-xl border border-blue-200 bg-blue-50 p-3">
        <AlertTriangle size={15} className="mt-0.5 shrink-0 text-blue-600" />
        <p className="text-xs text-blue-900">
          Receiving a vendor shipment? Use <strong>Receiving</strong> so cost and vendor history are recorded.
          Adjustments is for no-PO additions — found stock, corrections, recounts.
        </p>
      </div>

      <div className="rounded-xl border border-stone-200 bg-white p-5">
        <h3 className="text-sm font-bold text-stone-800 flex items-center gap-2 mb-1">
          <PackagePlus size={16} className="text-emerald-600" /> Check in stock
        </h3>
        <p className="text-[11px] text-stone-500 mb-4">
          Register units with no purchase order behind them. Leave serials blank to auto-generate placeholders.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-stone-500 mb-1 font-mono">Device model</label>
            <SearchableDropdown options={modelOptions} value={modelId || null} onChange={(v) => setModelId(v || '')} placeholder="-- Select --" variant="light" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-stone-500 mb-1 font-mono">Quantity</label>
            <input type="number" min={1} value={qty} onChange={(e) => setQty(e.target.value)} className="w-full px-3 py-2.5 bg-white border border-stone-200 rounded-lg text-stone-800 text-sm min-h-[44px]" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-stone-500 mb-1 font-mono">Serials (one per line, optional)</label>
            <textarea
              value={serials}
              onChange={(e) => setSerials(e.target.value)}
              placeholder={'K10-88231\nK10-88232'}
              rows={2}
              className="w-full px-3 py-2.5 bg-white border border-stone-200 rounded-lg text-stone-800 text-xs font-mono resize-none"
            />
            {parsedSerials.length > 0 && Number.isFinite(qtyNum) && (
              <p className="text-[10px] text-stone-500 mt-1">
                {Math.min(parsedSerials.length, qtyNum)} real serial(s), {Math.max(0, qtyNum - parsedSerials.length)} auto-generated
              </p>
            )}
          </div>
        </div>
        {error && <p className="mt-2 text-xs text-red-700 flex items-center gap-1.5"><AlertTriangle size={13} /> {error}</p>}
        <button
          onClick={submit}
          disabled={saving}
          className="mt-4 flex items-center gap-2 px-4 py-2.5 rounded-lg text-white text-sm font-semibold disabled:opacity-50 min-h-[44px]"
          style={{ background: '#DC143C' }}
        >
          {saving ? <RubyGemMark size={15} drawing color="currentColor" /> : <Check size={15} />}
          Check in {Number.isFinite(qtyNum) && qtyNum > 0 ? qtyNum : ''} unit{qtyNum === 1 ? '' : 's'} → in_stock
        </button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// VENDORS — carried over unchanged.
// ---------------------------------------------------------------------------

const VendorsPanel: React.FC = () => {
  const [vendors, setVendors] = useState<VendorRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [notBuilt, setNotBuilt] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Partial<VendorRow> | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null); setNotBuilt(false);
    try {
      const res = await inventoryApi.listVendors();
      setVendors(res.items || []);
    } catch (e: any) {
      if (String(e?.message || '').includes('404')) setNotBuilt(true);
      else setError(e?.message || 'Failed to load vendors');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!editing?.name) return;
    setSaving(true);
    try {
      await inventoryApi.upsertVendor({
        ...editing,
        // an all-blank contact row is UI scaffolding, not data
        contacts: (editing.contacts || []).filter((c) => (c.name || '').trim() || c.phone || c.email),
      });
      setEditing(null);
      await load();
    } catch (e: any) {
      setError(e?.message || 'Failed to save vendor');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <RubyLoader variant="page" />;

  return (
    <div>
      <div className="flex justify-end mb-4">
        <button
          onClick={() => setEditing({ name: '', active: true })}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-medium min-h-[44px]"
          style={{ background: '#DC143C' }}
        >
          <Plus size={16} /> Vendor
        </button>
      </div>
      {error && <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error}</div>}
      {notBuilt ? <NotBuiltYet what="Vendors" /> : vendors.length === 0 ? (
        <div className="rounded-xl border border-dashed border-stone-300 bg-white py-16 text-center">
          <Building2 size={28} className="mx-auto mb-3 text-stone-300" />
          <p className="text-sm font-medium text-stone-700">No vendors yet</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {vendors.map((v) => (
            <div key={v.vendorId} onClick={() => setEditing(v)} className="cursor-pointer rounded-xl border border-stone-200 bg-white p-4 hover:border-stone-300">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-stone-900 flex items-center justify-center shrink-0"><Building2 size={18} className="text-stone-300" /></div>
                <div className="min-w-0 flex-1">
                  <div className="font-bold text-stone-900 text-sm">{v.name}</div>
                  <div className="text-[11px] text-stone-500 mt-0.5">{[v.paymentTerms, v.leadTimeDays ? `lead ${v.leadTimeDays}d` : null].filter(Boolean).join(' · ') || '—'}</div>
                  {(v.phone || v.website || (v.contacts?.length ?? 0) > 0) && (
                    <div className="text-[11px] text-stone-500 mt-0.5 flex items-center gap-2 flex-wrap">
                      {v.phone && <span className="font-mono">{v.phone}</span>}
                      {v.website && (
                        <a
                          href={v.website.startsWith('http') ? v.website : `https://${v.website}`}
                          target="_blank" rel="noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="text-red-500 hover:underline"
                        >
                          {v.website.replace(/^https?:\/\//, '')}
                        </a>
                      )}
                      {(v.contacts?.length ?? 0) > 0 && <span>{v.contacts!.length} contact{v.contacts!.length === 1 ? '' : 's'}</span>}
                    </div>
                  )}
                </div>
                <span className={`text-[10px] font-semibold rounded-full px-2 py-0.5 border shrink-0 ${
                  v.active === false ? 'bg-stone-100 text-stone-500 border-stone-200' : 'bg-emerald-100 text-emerald-700 border-emerald-200'
                }`}>{v.active === false ? 'inactive' : 'active'}</span>
              </div>
              {(typeof v.onTimePct === 'number' || typeof v.defectPct === 'number') && (
                <p className="text-[11px] text-stone-500 mt-2">
                  {typeof v.onTimePct === 'number' && `on-time ${Math.round(v.onTimePct * 100)}%`}
                  {typeof v.onTimePct === 'number' && typeof v.defectPct === 'number' && ' · '}
                  {typeof v.defectPct === 'number' && `defect ${Math.round(v.defectPct * 100)}%`}
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-lg rounded-2xl bg-white text-gray-900 shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between bg-black px-5 py-4 text-white shrink-0">
              <h2 className="text-base font-bold">{editing.vendorId ? 'Edit vendor' : 'New vendor'}</h2>
              <button onClick={() => setEditing(null)}><X size={18} /></button>
            </div>
            <div className="p-5 space-y-3 overflow-y-auto">
              <input value={editing.name || ''} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="Vendor name" className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm min-h-[44px]" />
              <input value={editing.address || ''} onChange={(e) => setEditing({ ...editing, address: e.target.value })} placeholder="Address" className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm min-h-[44px]" />
              <div className="grid grid-cols-2 gap-3">
                <input value={editing.phone || ''} onChange={(e) => setEditing({ ...editing, phone: e.target.value })} placeholder="Rep phone" className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm min-h-[44px]" />
                <input value={editing.website || ''} onChange={(e) => setEditing({ ...editing, website: e.target.value })} placeholder="Website" className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm min-h-[44px]" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input value={editing.paymentTerms || ''} onChange={(e) => setEditing({ ...editing, paymentTerms: e.target.value })} placeholder="Payment terms (e.g. net-30)" className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm min-h-[44px]" />
                <input type="number" value={editing.leadTimeDays ?? ''} onChange={(e) => setEditing({ ...editing, leadTimeDays: e.target.value ? Number(e.target.value) : undefined })} placeholder="Lead time (days)" className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm min-h-[44px]" />
              </div>
              <SearchableDropdown
                options={[{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }]}
                value={editing.active === false ? 'inactive' : 'active'}
                onChange={(v) => setEditing({ ...editing, active: v !== 'inactive' })}
                variant="light"
              />
              <div className="pt-1">
                <div className="flex items-center justify-between mb-1.5">
                  <p className="text-xs font-semibold text-gray-700">Contacts</p>
                  <button
                    onClick={() => setEditing({ ...editing, contacts: [...(editing.contacts || []), { name: '' }] })}
                    className="text-xs font-semibold text-red-500"
                  >
                    + Add contact
                  </button>
                </div>
                {(editing.contacts || []).length === 0 && (
                  <p className="text-[11px] text-gray-400">No contacts yet — add your rep so their number is one click away.</p>
                )}
                <div className="space-y-2">
                  {(editing.contacts || []).map((c, i) => (
                    <div key={i} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 items-center">
                      <input value={c.name} onChange={(e) => setEditing({ ...editing, contacts: (editing.contacts || []).map((x, idx) => idx === i ? { ...x, name: e.target.value } : x) })} placeholder="Name" className="px-2.5 py-2 border border-gray-300 rounded-lg text-xs min-h-[40px]" />
                      <input value={c.phone || ''} onChange={(e) => setEditing({ ...editing, contacts: (editing.contacts || []).map((x, idx) => idx === i ? { ...x, phone: e.target.value } : x) })} placeholder="Phone" className="px-2.5 py-2 border border-gray-300 rounded-lg text-xs min-h-[40px]" />
                      <input value={c.email || ''} onChange={(e) => setEditing({ ...editing, contacts: (editing.contacts || []).map((x, idx) => idx === i ? { ...x, email: e.target.value } : x) })} placeholder="Email" className="px-2.5 py-2 border border-gray-300 rounded-lg text-xs min-h-[40px]" />
                      <button
                        onClick={() => setEditing({ ...editing, contacts: (editing.contacts || []).filter((_, idx) => idx !== i) })}
                        className="p-2 rounded-lg text-stone-400 hover:bg-stone-100"
                        title="Remove contact"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 border-t border-gray-200 px-5 py-4">
              <button onClick={() => setEditing(null)} className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium">Cancel</button>
              <button onClick={save} disabled={saving || !editing.name} className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-white text-sm font-semibold disabled:opacity-40" style={{ background: '#DC143C' }}>
                {saving ? <RubyGemMark size={14} drawing color="currentColor" /> : <Save size={14} />} Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// GRN — Goods Received, now paginated (Load more via limit+nextToken).
// ---------------------------------------------------------------------------

/**
 * WHAT AN OPERATOR CALLS THIS GRN.
 *
 * `grnId` is `grn_<uuid>` — unreadable, unsayable over the phone, and useless
 * on a printed packing note. The server now mints a sequential `grnNumber`
 * (GRN-YYYY-NNNN); this renders that when present and degrades to a short
 * uuid tail for every GRN created before the field existed, so old rows stay
 * identifiable rather than showing an empty column.
 */
export const grnLabel = (g: { grnNumber?: string; grnId: string }): string =>
  g.grnNumber || `GRN-${g.grnId.replace(/^grn_/, '').slice(0, 8).toUpperCase()}`;

/**
 * The header every in-page Receiving screen wears, in place of the old drawer's
 * black bar. Back is a real control, not an X on an overlay — it pops the
 * `?view=` param, so the browser's own Back button does the same thing.
 */
const ScreenHeader: React.FC<{
  crumb: string; title: string; sub?: string; onBack: () => void; right?: React.ReactNode;
}> = ({ crumb, title, sub, onBack, right }) => (
  <div className="mb-4">
    <button
      onClick={onBack}
      className="mb-2 inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-[12px] font-semibold text-stone-500 hover:bg-stone-100 hover:text-stone-800"
    >
      <ArrowLeft size={14} /> Receiving
    </button>
    <div className="flex flex-wrap items-end justify-between gap-3 border-b-2 border-stone-900 pb-2.5">
      <div className="min-w-0">
        <div className="font-mono text-[10px] uppercase tracking-wider text-stone-400">{crumb}</div>
        <h2 className="truncate text-xl font-bold tracking-tight text-stone-900">{title}</h2>
        {sub && <p className="mt-0.5 text-[12px] text-stone-500">{sub}</p>}
      </div>
      {right}
    </div>
    <div className="h-[3px] w-32 bg-gradient-to-r from-[#DC143C] to-[#9F1239]" />
  </div>
);

const GrnPanel: React.FC<{
  models: DeviceModelRow[];
  vendors: VendorRow[];
  units: DeviceUnitRow[];
  skus: BillingSkuRow[];
  onEditModel: (modelId: string) => void;
  onDuplicateModel: (modelId: string) => void;
  reloadModels: () => void;
}> = ({ models, vendors, units, skus, onEditModel, onDuplicateModel, reloadModels }) => {
  // The shared quick view, reached from a search result or a basket row.
  const [inspectId, setInspectId] = useState<string | null>(null);
  /**
   * Stock counters for the ONE model being inspected.
   *
   * `stockByModel` lives inside StockTab, not on the page, so there is nothing
   * to pass down here. Fetching the single model on open is cheaper than
   * lifting that state through the page — and showing the quick view a `{}`
   * would render "0 on hand" for a device that has plenty, which is worse than
   * slow in a screen whose whole job is counting stock.
   */
  const [inspectStock, setInspectStock] = useState<Record<string, { onHand: number; reserved?: number } | undefined>>({});
  useEffect(() => {
    if (!inspectId || inspectStock[inspectId]) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await inventoryApi.stockSummary([inspectId]);
        if (!cancelled) setInspectStock((prev) => ({ ...prev, ...res }));
      } catch { /* the popup shows its unknown state rather than a wrong count */ }
    })();
    return () => { cancelled = true; };
  }, [inspectId, inspectStock]);
  const [grns, setGrns] = useState<GrnRow[]>([]);
  const [nextToken, setNextToken] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [notBuilt, setNotBuilt] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /**
   * WHICH SCREEN, IN THE URL — not in a drawer.
   *
   * Receiving used to open every screen as a fixed slide-over. Three problems
   * an operator actually hits: the list behind it is unusable while it is open,
   * browser Back closes the whole page instead of the panel, and a half-finished
   * GRN cannot be linked to anyone. A `?view=` param fixes all three and costs
   * one search param.
   *
   *   absent   -> the GRN list
   *   'new'    -> create a GRN
   *   'photos' -> device photo backfill
   *   <grnId>  -> receive against that GRN
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const view = searchParams.get('view');
  const setView = useCallback((next: string | null) => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      if (next) p.set('view', next); else p.delete('view');
      return p;
    }, { replace: false });
  }, [setSearchParams]);
  const creating = view === 'new';
  const backfilling = view === 'photos';
  const openGrn = view && view !== 'new' && view !== 'photos' ? view : null;
  const [newVendorId, setNewVendorId] = useState('');
  const [newPo, setNewPo] = useState('');
  const [newExpectedAt, setNewExpectedAt] = useState('');
  const [lines, setLines] = useState<GrnLineInput[]>([{ modelId: '', qty: 1, unitCostCents: 0 }]);
  const [saving, setSaving] = useState(false);
  // Per-LINE receiving state (screen 2 — each line receives in its own way).
  const [lineSerial, setLineSerial] = useState<Record<number, string>>({});
  const [lineQty, setLineQty] = useState<Record<number, string>>({});
  const [damagedOpenLine, setDamagedOpenLine] = useState<number | null>(null);
  const [damagedForm, setDamagedForm] = useState<{ reason: string; chargedTo: 'vendor' | 'tenant' | 'writeoff'; amountCents: number; notes: string }>(
    { reason: '', chargedTo: 'vendor', amountCents: 0, notes: '' },
  );
  const [lineBusy, setLineBusy] = useState<number | null>(null);
  const [justReceived, setJustReceived] = useState<Record<string, { unitId: string; trackingId: string }[]>>({});
  const [printingUnitId, setPrintingUnitId] = useState<string | null>(null);

  // Date filter. Empty means unbounded — the list opens showing everything.
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError(null); setNotBuilt(false);
    try {
      const res = await inventoryApi.listGrns({ limit: 20, from: fromDate || undefined, to: toDate || undefined });
      setGrns(res.items || []);
      setNextToken(res.nextToken);
    } catch (e: any) {
      if (String(e?.message || '').includes('404')) setNotBuilt(true);
      else setError(e?.message || 'Failed to load GRNs');
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate]);
  useEffect(() => { load(); }, [load]);

  const loadMore = async () => {
    if (!nextToken) return;
    setLoadingMore(true);
    try {
      const res = await inventoryApi.listGrns({ limit: 20, nextToken, from: fromDate || undefined, to: toDate || undefined });
      setGrns((prev) => [...prev, ...(res.items || [])]);
      setNextToken(res.nextToken);
    } catch (e: any) {
      setError(e?.message || 'Failed to load more GRNs');
    } finally {
      setLoadingMore(false);
    }
  };

  const modelOf = (id: string) => models.find((m) => m.modelId === id);
  const modelName = (id: string) => modelOf(id)?.modelName || id;

  /**
   * Persists a unit's note and cost, then asks the page to refetch so the row
   * shows the stored values rather than the ones still in the input.
   */
  const saveUnit = async (
    unitId: string,
    patch: {
      notes: string;
      notesRich?: { ops: any[] };
      badges?: string[];
      costCents: number;
      costBreakdown: { unitCostCents: number; inboundShippingCents: number; dutyCents: number };
      /* Typed or scanned off the sticker while receiving — see
         `ReceivedUnitCard`'s identity block. */
      serialNumber?: string;
    },
  ) => {
    const unit = units.find((u) => u.unitId === unitId);
    if (!unit) throw new Error('unit not found');
    await hardwareApi.saveUnitNoteAndCost(unitId, unit.modelId, patch);
    reloadModels();
  };

  /**
   * A label photo is written by its OWN endpoint, not through `saveUnit`, so
   * the card hands the updated unit back and the list has to be refreshed for
   * the "Required" badge to clear.
   *
   * `units` is a prop here, so this reloads rather than patching in place —
   * the same thing `saveUnit` above already does after every note and cost.
   * The card keeps the returned unit in its own state meanwhile, so the
   * thumbnail appears immediately and does not wait for the round trip.
   */
  const applyUnitChange = (_next: DeviceUnitRow) => { reloadModels(); };

  const printLabel = async (unitId: string) => {
    setPrintingUnitId(unitId);
    try { await printUnitLabel(unitId); } catch (e: any) { setError(e?.message || 'Could not prepare the label'); }
    finally { setPrintingUnitId(null); }
  };

  const createGrn = async () => {
    const validLines = lines.filter((l) => l.modelId && l.qty > 0);
    if (!newVendorId || validLines.length === 0) return;
    setSaving(true);
    try {
      await inventoryApi.createGrn({
        vendorId: newVendorId, poRef: newPo || undefined, lines: validLines,
        expectedAt: newExpectedAt ? new Date(newExpectedAt).getTime() : undefined,
      });
      setView(null);
      setNewVendorId(''); setNewPo(''); setNewExpectedAt(''); setLines([{ modelId: '', qty: 1, unitCostCents: 0 }]);
      await load();
    } catch (e: any) {
      setError(e?.message || 'Failed to create GRN');
    } finally {
      setSaving(false);
    }
  };

  const receiveUnit = async (grnId: string, modelId: string, lineIdx: number) => {
    setLineBusy(lineIdx);
    try {
      const res = await inventoryApi.receiveGrnUnit(grnId, { modelId, serial: lineSerial[lineIdx]?.trim() || undefined });
      if (res?.unit?.unitId) {
        setJustReceived((prev) => ({
          ...prev,
          [grnId]: [{ unitId: res.unit.unitId, trackingId: res.trackingId || res.unit.trackingId || '' }, ...(prev[grnId] || [])],
        }));
      }
      setLineSerial((prev) => ({ ...prev, [lineIdx]: '' }));
      // BOTH loads: `load()` refreshes the GRN's progress counters, and
      // `reloadModels()` refreshes the page's `units` — which is what the
      // received-unit cards render from. Without the second, a unit scanned
      // moments ago would not appear until the page was reloaded.
      await Promise.all([load(), reloadModels()]);
    } catch (e: any) {
      setError(e?.message || 'Failed to receive unit');
    } finally {
      setLineBusy(null);
    }
  };

  const receiveDamaged = async (grnId: string, modelId: string, lineIdx: number) => {
    if (!damagedForm.reason.trim()) return;
    setLineBusy(lineIdx);
    try {
      await inventoryApi.receiveGrnUnit(grnId, {
        modelId, serial: lineSerial[lineIdx]?.trim() || undefined, damaged: true,
        reason: damagedForm.reason.trim(), chargedTo: damagedForm.chargedTo,
        amountCents: damagedForm.amountCents || undefined, notes: damagedForm.notes.trim() || undefined,
      });
      toastService.success('Damaged arrival logged — see the Damage tab.');
      setDamagedOpenLine(null);
      setDamagedForm({ reason: '', chargedTo: 'vendor', amountCents: 0, notes: '' });
      setLineSerial((prev) => ({ ...prev, [lineIdx]: '' }));
      await load();
    } catch (e: any) {
      setError(e?.message || 'Failed to log the damaged arrival');
    } finally {
      setLineBusy(null);
    }
  };

  const receiveBulk = async (grnId: string, modelId: string, lineIdx: number, qty: number) => {
    if (!qty || qty < 1) return;
    setLineBusy(lineIdx);
    try {
      await inventoryApi.receiveGrnBulk(grnId, { modelId, qty });
      toastService.success(`Added ${qty} to stock.`);
      setLineQty((prev) => ({ ...prev, [lineIdx]: '' }));
      await load();
    } catch (e: any) {
      setError(e?.message || 'Failed to receive this line');
    } finally {
      setLineBusy(null);
    }
  };

  if (loading) return <RubyLoader variant="page" />;

  const openG = openGrn ? grns.find((g) => g.grnId === openGrn) : null;

  return (
    <div>
      {!view && (
      <div className="flex items-center justify-end gap-2 mb-4">
        <button
          onClick={() => setView('photos')}
          className="flex min-h-[44px] items-center gap-2 rounded-lg border border-stone-300 bg-white px-3 py-2 text-[12.5px] font-semibold text-stone-700 hover:bg-stone-50"
          title="Add or replace the picture on any device"
        >
          <ImageOff size={15} /> Device photos
        </button>
        <button onClick={() => setView('new')} className="flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-medium min-h-[44px]" style={{ background: '#DC143C' }}>
          <Plus size={16} /> Create PO
        </button>
      </div>
      )}
      {error && <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error}</div>}
      {!view && (
      <>
      <div className="mb-3 flex flex-wrap items-end gap-2 rounded-xl border border-stone-200 bg-white p-3">
        <div>
          <label htmlFor="grn-from" className="mb-1 block font-mono text-[9.5px] uppercase tracking-wider text-stone-500">Received from</label>
          <input id="grn-from" type="date" value={fromDate} max={toDate || undefined}
            onChange={(e) => setFromDate(e.target.value)}
            className="min-h-[40px] rounded-lg border border-stone-300 px-2.5 text-[13px] outline-none focus:border-[#DC143C]" />
        </div>
        <div>
          <label htmlFor="grn-to" className="mb-1 block font-mono text-[9.5px] uppercase tracking-wider text-stone-500">To</label>
          <input id="grn-to" type="date" value={toDate} min={fromDate || undefined}
            onChange={(e) => setToDate(e.target.value)}
            className="min-h-[40px] rounded-lg border border-stone-300 px-2.5 text-[13px] outline-none focus:border-[#DC143C]" />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {([
            ['Last 7 days', 7], ['Last 30 days', 30], ['Last 90 days', 90],
          ] as [string, number][]).map(([label, days]) => (
            <button
              key={days}
              onClick={() => {
                const end = new Date();
                const start = new Date(end.getTime() - (days - 1) * 86400000);
                setFromDate(start.toISOString().slice(0, 10));
                setToDate(end.toISOString().slice(0, 10));
              }}
              className="min-h-[40px] rounded-lg border border-stone-300 bg-white px-2.5 text-[12px] font-semibold text-stone-600 hover:border-stone-400 hover:bg-stone-50"
            >{label}</button>
          ))}
        </div>
        {(fromDate || toDate) && (
          <button
            onClick={() => { setFromDate(''); setToDate(''); }}
            className="ml-auto min-h-[40px] rounded-lg px-3 text-[12px] font-semibold text-[#DC143C] hover:bg-[#FFF1F3]"
          >Clear dates</button>
        )}
      </div>
      {notBuilt ? <NotBuiltYet what="GRN receiving" /> : grns.length === 0 ? (
        <div className="rounded-xl border border-dashed border-stone-300 bg-white py-16 text-center">
          <ClipboardList size={28} className="mx-auto mb-3 text-stone-300" />
          <p className="text-sm font-medium text-stone-700">
            {fromDate || toDate ? 'No GRNs in that date range' : 'No goods-received notes yet'}
          </p>
          {(fromDate || toDate) && (
            <button onClick={() => { setFromDate(''); setToDate(''); }} className="mt-2 text-xs font-semibold text-[#DC143C]">
              Clear the dates
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {grns.map((g) => {
            const vendorName = vendors.find((v) => v.vendorId === g.vendorId)?.name || g.vendorId;
            const receivedCount = (g.progress?.receivedQty ?? 0) + (g.progress?.damagedQty ?? 0);
            const totalQty = g.progress?.totalQty ?? g.lines.reduce((s, l) => s + l.qty, 0);
            return (
              <div
                key={g.grnId}
                onClick={() => setView(g.grnId)}
                className="flex items-center gap-3 p-4 rounded-xl border border-stone-200 bg-white cursor-pointer hover:border-stone-300"
              >
                <span className="font-mono text-sm font-semibold text-[#DC143C]">{grnLabel(g)}</span>
                <span className="text-sm text-stone-700">{vendorName}</span>
                {g.poRef && <span className="text-xs text-stone-400">PO {g.poRef}</span>}
                <span className="text-xs text-stone-500">{totalQty} unit(s)</span>
                {g.createdAt && (
                  <span className="text-xs text-stone-400">
                    {new Date(g.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </span>
                )}
                <span className={`ml-auto text-[10px] font-semibold rounded-full px-2 py-0.5 border ${
                  g.status === 'received' ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
                  : g.status === 'in_transit' ? 'bg-amber-100 text-amber-700 border-amber-200'
                  : 'bg-stone-100 text-stone-600 border-stone-200'
                }`}>{receivedCount}/{totalQty} received</span>
              </div>
            );
          })}
          {nextToken && (
            <div className="text-center pt-1">
              <button
                onClick={loadMore}
                disabled={loadingMore}
                className="px-4 py-2 rounded-lg border border-stone-300 bg-white text-stone-700 text-xs font-semibold disabled:opacity-50"
              >
                {loadingMore ? <span className="inline-flex"><RubyGemMark size={13} drawing color="currentColor" /></span> : 'Load more'}
              </button>
            </div>
          )}
        </div>
      )}
      </>
      )}

      {/* Screen 2 — Receive, in the page. */}
      {openG && (
        <div>
          <ScreenHeader
            crumb={`Receiving · ${grnLabel(openG)}`}
            title={`Receive — ${vendors.find((v) => v.vendorId === openG.vendorId)?.name || openG.vendorId}`}
            sub={`${openG.lines.length} line${openG.lines.length === 1 ? '' : 's'}${openG.poRef ? ` · PO #${openG.poRef}` : ''}${
              openG.expectedAt ? ` · expected ${new Date(openG.expectedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}` : ''
            }`}
            onBack={() => setView(null)}
          />
          <div>
            <div className="space-y-3">
              {openG.lines.map((l, i) => {
                const m = modelOf(l.modelId);
                const quantity = m ? isQty(m) : false;
                const remaining = l.remainingQty ?? l.qty;
                const busy = lineBusy === i;
                const qty = Number.parseInt(lineQty[i] ?? '', 10) || Math.min(remaining, 1);
                return (
                  <div key={i} className="rounded-xl border border-stone-200 bg-white p-3">
                    <div className="flex items-center gap-2.5">
                      {/* The photo is the fastest way to confirm the box in your
                          hands is the line you are receiving against. */}
                      <button
                        type="button"
                        onClick={() => setInspectId(l.modelId)}
                        title={`Details for ${modelName(l.modelId)}`}
                        className="shrink-0"
                      >
                        <ShimmerImage
                          src={m?.media?.heroImageUrl}
                          alt={modelName(l.modelId)}
                          width={40}
                          height={40}
                          objectFit="contain"
                          variant="light"
                          className="rounded-lg border border-stone-200 bg-stone-50"
                          fallbackBgColor="bg-stone-50"
                          fallbackIcon={<Package size={16} className="text-stone-300" />}
                        />
                      </button>
                      <span className={`rounded-full px-2 py-0.5 text-[9.5px] font-semibold ${TRACKING_CHIP_CLASS[quantity ? 'quantity' : 'serialized']}`}>
                        {quantity ? 'QUANTITY' : 'SERIALIZED'}
                      </span>
                      <b className="text-[12.5px]">{modelName(l.modelId)}</b>
                      <span className="font-mono text-[11px] text-stone-500">
                        {money(l.unitCostCents)}/{quantity ? 'pc' : 'unit'}
                        {quantity && m?.sharedBarcode ? ` · ${m.sharedBarcode}` : ''}
                      </span>
                      <span className="ml-auto font-mono text-[11px] text-stone-500">{remaining} left</span>
                    </div>

                    {remaining <= 0 ? (
                      <p className="mt-2 text-[11px] text-emerald-700">Fully received.</p>
                    ) : quantity ? (
                      <div className="mt-2.5 flex items-center gap-2.5">
                        <span className="text-[11px] text-stone-500">Receive</span>
                        <div className="inline-flex items-center overflow-hidden rounded-lg border border-stone-200">
                          <button
                            onClick={() => setLineQty((prev) => ({ ...prev, [i]: String(Math.max(1, qty - 1)) }))}
                            className="w-7 h-8 bg-stone-50 text-stone-700 hover:bg-stone-100"
                          ><Minus size={12} className="mx-auto" /></button>
                          <input
                            value={lineQty[i] ?? String(Math.min(remaining, qty))}
                            onChange={(e) => setLineQty((prev) => ({ ...prev, [i]: e.target.value.replace(/\D/g, '') }))}
                            className="w-14 text-center font-mono text-xs py-1.5 outline-none"
                          />
                          <button
                            onClick={() => setLineQty((prev) => ({ ...prev, [i]: String(Math.min(remaining, qty + 1)) }))}
                            className="w-7 h-8 bg-stone-50 text-stone-700 hover:bg-stone-100"
                          ><Plus size={12} className="mx-auto" /></button>
                        </div>
                        <button
                          onClick={() => receiveBulk(openG.grnId, l.modelId, i, Math.min(remaining, qty))}
                          disabled={busy}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-white text-[11px] font-semibold disabled:opacity-50"
                          style={{ background: '#DC143C' }}
                        >
                          {busy ? <RubyGemMark size={12} drawing color="currentColor" /> : null}
                          Add {Math.min(remaining, qty)} to stock
                        </button>
                      </div>
                    ) : (
                      <>
                        <div className="mt-2.5 flex items-center gap-2">
                          <input
                            value={lineSerial[i] || ''}
                            onChange={(e) => setLineSerial((prev) => ({ ...prev, [i]: e.target.value }))}
                            onKeyDown={(e) => { if (e.key === 'Enter' && !busy) receiveUnit(openG.grnId, l.modelId, i); }}
                            /* Names WHOSE number this is. Scanning the box and
                               scanning our own label are different acts, and a
                               bare "serial" invited the second. */
                            placeholder="Scan / type the DEVICE serial (the manufacturer's) — Enter accepts it and mints our RTA barcode"
                            className="flex-1 px-3 py-2 border border-stone-200 rounded-lg text-xs font-mono"
                          />
                          <button
                            onClick={() => setDamagedOpenLine(damagedOpenLine === i ? null : i)}
                            className="px-3 py-2 rounded-lg border border-stone-300 bg-white text-stone-700 text-[11px] font-semibold whitespace-nowrap"
                          >
                            Damaged arrival…
                          </button>
                        </div>

                        {damagedOpenLine === i && (
                          <div className="mt-2.5 rounded-lg border border-red-200 bg-red-50 p-3 space-y-2">
                            <input
                              value={damagedForm.reason}
                              onChange={(e) => setDamagedForm((f) => ({ ...f, reason: e.target.value }))}
                              placeholder="Reason (e.g. crushed box in transit)"
                              className="w-full px-2.5 py-1.5 border border-red-200 rounded-lg text-xs bg-white"
                            />
                            <div className="flex gap-2">
                              <div className="flex-1">
                                <SearchableDropdown
                                  options={[{ value: 'vendor', label: 'Charged to vendor' }, { value: 'tenant', label: 'Charged to tenant' }, { value: 'writeoff', label: 'Write-off' }]}
                                  value={damagedForm.chargedTo}
                                  onChange={(v) => setDamagedForm((f) => ({ ...f, chargedTo: (v as any) || 'vendor' }))}
                                  variant="light" compact
                                />
                              </div>
                              <input
                                type="number" min={0} step={0.01}
                                value={damagedForm.amountCents ? (damagedForm.amountCents / 100).toFixed(2) : ''}
                                onChange={(e) => setDamagedForm((f) => ({ ...f, amountCents: Math.round(Number(e.target.value || 0) * 100) }))}
                                placeholder="Amount $"
                                className="w-28 px-2.5 py-1.5 border border-red-200 rounded-lg text-xs bg-white"
                              />
                            </div>
                            <button
                              onClick={() => receiveDamaged(openG.grnId, l.modelId, i)}
                              disabled={busy || !damagedForm.reason.trim()}
                              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-600 text-white text-[11px] font-semibold disabled:opacity-40"
                            >
                              {busy ? <RubyGemMark size={12} drawing color="currentColor" /> : null} Log damaged arrival
                            </button>
                          </div>
                        )}

                        {(() => {
                          // Every unit already received against THIS line, not
                          // just this session's scans: `units` is loaded by the
                          // page, so a refresh no longer empties the list. Newest
                          // first, matching the order they were scanned in.
                          const received = units
                            .filter((u) => u.grnId === openG.grnId && u.modelId === l.modelId)
                            .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
                          if (received.length === 0) return null;
                          const fresh = new Set((justReceived[openG.grnId] || []).map((j) => j.unitId));
                          return (
                            <div className="mt-3 rounded-xl border border-stone-100 bg-stone-50 p-2.5">
                              <div className="mb-2 font-mono text-[9.5px] uppercase tracking-wider text-stone-400">
                                Received · {received.length}
                              </div>
                              <div className="space-y-1.5">
                                {received.map((u) => (
                                  <ReceivedUnitCard
                                    key={u.unitId}
                                    unit={u}
                                    model={m}
                                    grnNumber={grnLabel(openG)}
                                    vendorName={vendors.find((v) => v.vendorId === openG.vendorId)?.name}
                                    highlight={fresh.has(u.unitId)}
                                    defaultOpen={fresh.has(u.unitId)}
                                    onPrintLabel={printLabel}
                                    onInspectModel={(id) => setInspectId(id)}
                                    onSave={saveUnit}
                                    onUnitChanged={applyUnitChange}
                                  />
                                ))}
                              </div>
                            </div>
                          );
                        })()}
                      </>
                    )}
                  </div>
                );
              })}
              <p className="text-[11px] text-stone-500">
                Each accepted serialized unit mints a company tracking ID (RTA-######) and lands <code className="font-mono">in_stock</code>.
                A quantity line writes one signed <code className="font-mono">received</code> movement — no unit minted.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Screen 1 (procurement side) — create GRN, in the page. */}
      {creating && (
        <div>
          <ScreenHeader
            crumb="Receiving · New PO"
            title="Create PO — what the vendor is sending"
            sub="Raise the order now; receive against it as the boxes arrive."
            onBack={() => setView(null)}
          />
          <div>
            <div className="max-w-3xl space-y-3">
              <SearchableDropdown
                options={vendors.map((v) => ({ value: v.vendorId, label: v.name }))}
                value={newVendorId || null}
                onChange={(v) => setNewVendorId(v || '')}
                placeholder="-- Vendor --"
                variant="light"
              />
              <input value={newPo} onChange={(e) => setNewPo(e.target.value)} placeholder="PO reference (optional)" className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm min-h-[44px]" />
              <div>
                <label className="mb-1 block text-[10px] uppercase tracking-wider text-stone-500 font-mono">Expected date (optional)</label>
                <input type="date" value={newExpectedAt} onChange={(e) => setNewExpectedAt(e.target.value)} className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm min-h-[44px]" />
              </div>
              <GrnLineComposer
                models={models}
                lines={lines.filter((l) => l.modelId)}
                onChange={(next) => setLines(next.length ? next : [{ modelId: '', qty: 1, unitCostCents: 0 }])}
                onInspect={(id) => setInspectId(id)}
              />
            </div>
            <div className="mt-5 flex max-w-3xl justify-end gap-2 border-t border-gray-200 pt-4">
              <button onClick={() => setView(null)} className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium min-h-[44px]">Cancel</button>
              <button onClick={createGrn} disabled={saving || !newVendorId} className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-white text-sm font-semibold disabled:opacity-40" style={{ background: '#DC143C' }}>
                {saving ? <RubyGemMark size={14} drawing color="currentColor" /> : <Save size={14} />} Create GRN
              </button>
            </div>
          </div>
        </div>
      )}

      {inspectId && (
        <DeviceQuickView
          modelId={inspectId}
          models={models}
          units={units}
          skus={skus}
          stockByModel={inspectStock}
          onClose={() => setInspectId(null)}
          onEdit={(id) => { setInspectId(null); onEditModel(id); }}
          onDuplicate={(id) => { setInspectId(null); onDuplicateModel(id); }}
          onViewUnits={() => setInspectId(null)}
        />
      )}

      {backfilling && (
        <DeviceImageBackfill
          models={models}
          onClose={() => setView(null)}
          onSaved={reloadModels}
        />
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// DAMAGE & write-offs — paginated, "Log damage" launches the 3-step wizard.
// ---------------------------------------------------------------------------

const CHARGED_TO_CHIP: Record<string, string> = {
  vendor: 'bg-amber-100 text-amber-700 border-amber-200',
  tenant: 'bg-red-100 text-red-700 border-red-200',
  writeoff: 'bg-stone-100 text-stone-600 border-stone-200',
};
const CHARGED_TO_LABEL: Record<string, string> = { vendor: 'Vendor', tenant: 'Tenant', writeoff: 'Write-off' };

const STATUS_FILTERS: { id: DamageStatus | 'all'; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'open', label: 'Open' },
  { id: 'resolved', label: 'Resolved' },
  { id: 'cancelled', label: 'Cancelled' },
];

const DamagePanel: React.FC<{ models: DeviceModelRow[]; units: DeviceUnitRow[] }> = ({ models, units }) => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<DamageRow[]>([]);
  const [nextToken, setNextToken] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [notBuilt, setNotBuilt] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState<DamageStatus | 'all'>('all');

  const load = useCallback(async (status: DamageStatus | 'all') => {
    setLoading(true); setError(null); setNotBuilt(false);
    try {
      const res = await inventoryApi.listDamage({ limit: 20, status: status === 'all' ? undefined : status });
      setRows(res.items || []);
      setNextToken(res.nextToken);
    } catch (e: any) {
      if (String(e?.message || '').includes('404')) setNotBuilt(true);
      else setError(e?.message || 'Failed to load damage log');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(statusFilter); }, [load, statusFilter]);

  const loadMore = async () => {
    if (!nextToken) return;
    setLoadingMore(true);
    try {
      const res = await inventoryApi.listDamage({ limit: 20, status: statusFilter === 'all' ? undefined : statusFilter, nextToken });
      setRows((prev) => [...prev, ...(res.items || [])]);
      setNextToken(res.nextToken);
    } catch (e: any) {
      setError(e?.message || 'Failed to load more damage records');
    } finally {
      setLoadingMore(false);
    }
  };

  const onRowResolved = (result: DamageResolveResult) => {
    setRows((prev) => prev.map((d) => (d.unitId === result.damage.unitId && d.createdAt === result.damage.createdAt ? result.damage : d)));
  };

  // Resolve each damage row to the DEVICE it concerns — a bare unit id tells
  // an operator nothing. Unit-scoped rows resolve unit -> model; model-scoped
  // (bulk qty) rows carry modelId directly.
  const unitById = useMemo(() => new Map(units.map((u) => [u.unitId, u])), [units]);
  const modelNameById = useMemo(() => new Map(models.map((m) => [m.modelId, m.modelName])), [models]);
  const deviceCell = (d: DamageRow) => {
    const unit = d.unitId ? unitById.get(d.unitId) : undefined;
    const modelId = d.modelId || unit?.modelId;
    const name = (modelId && modelNameById.get(modelId)) || modelId || 'Unknown device';
    const ref = unit?.trackingId || unit?.serialNumber || d.unitId;
    return (
      <div className="min-w-0">
        <div className="text-xs font-medium text-stone-800 truncate">
          {name}
          {typeof d.qty === 'number' && <span className="text-stone-400"> × {d.qty}</span>}
        </div>
        {d.unitId ? (
          <button
            onClick={() => navigate(`/inventory/units/${encodeURIComponent(d.unitId!)}`)}
            className="font-mono text-[10.5px] text-red-500 hover:underline"
            title="Open this unit"
          >
            {ref}
          </button>
        ) : (
          <span className="text-[10.5px] text-stone-400">bulk stock</span>
        )}
      </div>
    );
  };

  if (loading) return <RubyLoader variant="page" />;

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-1.5 flex-wrap">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.id}
              onClick={() => setStatusFilter(f.id)}
              className={`px-3 py-1.5 rounded-full text-[11px] font-semibold border ${
                statusFilter === f.id ? 'bg-stone-900 text-white border-stone-900' : 'bg-white text-stone-600 border-stone-300'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <button onClick={() => setWizardOpen(true)} className="flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-medium min-h-[44px]" style={{ background: '#DC143C' }}>
          <Plus size={16} /> Log damage
        </button>
      </div>
      {error && <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error}</div>}
      {notBuilt ? <NotBuiltYet what="Damage log" /> : rows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-stone-300 bg-white py-16 text-center">
          <ShieldAlert size={28} className="mx-auto mb-3 text-stone-300" />
          <p className="text-sm font-medium text-stone-700">No damage recorded</p>
        </div>
      ) : (
        <>
          <div className="rounded-xl border border-stone-200 bg-white overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-stone-50 border-b border-stone-200">
                <tr className="text-left text-[10px] uppercase tracking-wide text-stone-500">
                  <th className="px-4 py-3 font-semibold">Device</th><th className="px-4 py-3 font-semibold">Stage</th>
                  <th className="px-4 py-3 font-semibold">Reason</th><th className="px-4 py-3 font-semibold">Charged to</th>
                  <th className="px-4 py-3 font-semibold">Amount</th><th className="px-4 py-3 font-semibold">Status</th>
                  <th className="px-4 py-3 font-semibold">Resolution / actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {rows.map((d) => (
                  <tr key={`${d.unitId}-${d.createdAt}`}>
                    <td className="px-4 py-3">{deviceCell(d)}</td>
                    <td className="px-4 py-3 text-stone-600 text-xs">{d.stage}</td>
                    <td className="px-4 py-3 text-stone-700">
                      {d.reason}
                      {d.notes && <div className="text-[11px] text-stone-400 mt-0.5">{d.notes}</div>}
                    </td>
                    <td className="px-4 py-3"><span className={`text-[10px] font-semibold rounded-full px-2 py-0.5 border ${CHARGED_TO_CHIP[d.chargedTo]}`}>{CHARGED_TO_LABEL[d.chargedTo]}</span></td>
                    <td className="px-4 py-3 text-stone-600 text-xs">{typeof d.amountCents === 'number' ? `$${(d.amountCents / 100).toFixed(2)}` : '—'}</td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] font-semibold rounded-full px-2 py-0.5 border ${DAMAGE_STATUS_CHIP[d.status]}`}>{DAMAGE_STATUS_LABEL[d.status]}</span>
                    </td>
                    <td className="px-4 py-3">
                      {d.status === 'open' ? (
                        <DamageResolveActions damage={d} onResolved={onRowResolved} />
                      ) : (
                        <DamageResolutionNote damage={d} />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {nextToken && (
            <div className="text-center pt-3">
              <button
                onClick={loadMore}
                disabled={loadingMore}
                className="px-4 py-2 rounded-lg border border-stone-300 bg-white text-stone-700 text-xs font-semibold disabled:opacity-50"
              >
                {loadingMore ? <span className="inline-flex"><RubyGemMark size={13} drawing color="currentColor" /></span> : 'Load more'}
              </button>
            </div>
          )}
        </>
      )}

      {wizardOpen && (
        <DamageWizard onClose={() => setWizardOpen(false)} onSaved={() => load(statusFilter)} />
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// BRANDS — carried over unchanged.
// ---------------------------------------------------------------------------

const BrandsPanel: React.FC<{ models: DeviceModelRow[] }> = ({ models }) => {
  const [brands, setBrands] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [notBuilt, setNotBuilt] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null); setNotBuilt(false);
    try {
      const res = await inventoryApi.listBrands();
      setBrands(res.items || []);
    } catch (e: any) {
      if (String(e?.message || '').includes('404')) setNotBuilt(true);
      else setError(e?.message || 'Failed to load brands');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const modelCount = (brand: string) => models.filter((m) => m.brand === brand).length;

  if (loading) return <RubyLoader variant="page" />;

  return (
    <div>
      {error && <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error}</div>}
      {notBuilt ? <NotBuiltYet what="Brands" /> : brands.length === 0 ? (
        <div className="rounded-xl border border-dashed border-stone-300 bg-white py-16 text-center">
          <TagIcon size={28} className="mx-auto mb-3 text-stone-300" />
          <p className="text-sm font-medium text-stone-700">No brands yet</p>
          <p className="mt-1 text-xs text-stone-500">
            Brands come from the <code className="font-mono">brand</code> field on device models — set it when
            creating or editing a model in the catalog.
          </p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {brands.map((b) => (
              <div key={b} className="rounded-xl border border-stone-200 bg-white p-4">
                <div className="font-bold text-stone-900 text-sm">{b}</div>
                <div className="text-[11px] text-stone-500 mt-1">{modelCount(b)} model(s)</div>
              </div>
            ))}
          </div>
          <p className="mt-3 text-[11px] text-stone-500">
            A brand is created by setting the <code className="font-mono">brand</code> field on a device model —
            there is nothing to create here directly.
          </p>
        </>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// SHELL
// ---------------------------------------------------------------------------

/**
 * `tab` (route mode): the section this route renders — the side nav is the
 * navigation, so no in-page tab bar. `defaultTab` (legacy mode, no `tab`):
 * the old merged-hub mounts (`/inventory/catalog`, `/inventory/procurement`)
 * — these immediately redirect to the section's own route, honouring `?tab=`.
 */
const InventoryHubPage: React.FC<{ tab?: string; defaultTab?: string }> = ({ tab: tabProp, defaultTab = 'stock' }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Legacy mount → redirect to the section's dedicated route, keeping old
  // links and in-app navigate('/inventory/catalog') calls working.
  const legacyTarget = tabProp ? null : sectionFor(searchParams.get('tab') || defaultTab).route;
  useEffect(() => {
    if (legacyTarget) navigate(legacyTarget, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [legacyTarget]);

  const tab = tabProp || defaultTab;
  const setTab = (id: string) => navigate(sectionFor(id).route);

  const [models, setModels] = useState<DeviceModelRow[]>([]);
  const [invalidModels, setInvalidModels] = useState<string[]>([]);
  const [allUnits, setAllUnits] = useState<DeviceUnitRow[]>([]);
  const [vendors, setVendors] = useState<VendorRow[]>([]);
  const [skus, setSkus] = useState<BillingSkuRow[]>([]);
  /**
   * FINANCING, MERGED IN (2026-08-30). Stock and Financing are one screen now,
   * so the shell loads the agreement book and the FULL offer catalog alongside
   * the device data.
   *
   * `allSkus` is deliberately separate from `skus`: `skus` is filtered to
   * ACTIVE offers because the row chips count what is currently sellable,
   * while the policies section must also show RETIRED offers so they can be
   * re-offered or deleted. Filtering both the same way is how a retired offer
   * becomes unreachable.
   */
  const [allSkus, setAllSkus] = useState<BillingSkuRow[]>([]);
  const [bundles, setBundles] = useState<DeviceBundleRow[]>([]);
  /**
   * The WHOLE agreement book — every status, paged to exhaustion (see
   * `fetchWholeAgreementBook` below), not one page of it. `FinancingPoliciesSection`'s
   * per-offer rollups and its SKU delete-refusal check both depend on this
   * being complete: see `agreementsComplete`.
   */
  const [agreements, setAgreements] = useState<FinanceAgreementRow[]>([]);
  const [financingGeneratedAt, setFinancingGeneratedAt] = useState<number | null>(null);
  const [financingError, setFinancingError] = useState<string | null>(null);
  /**
   * Whether `agreements` above actually finished paging to exhaustion. Starts
   * `false` (unknown), is reset to `false` at the START of every
   * `loadAgreementBook` run (not just on mount), and only flips `true` once
   * that SAME run's every page has been read via `nextToken`. Threaded into
   * `FinancingPoliciesSection` so its delete gate refuses rather than trusts
   * a partial — or stale-in-flight — list. See Ruling 12 and Ruling 13.
   */
  const [agreementsComplete, setAgreementsComplete] = useState(false);
  /** Request-sequence guard for `loadAgreementBook` — see its own comment. */
  const agreementBookSeq = useRef(0);
  /**
   * THE DEVICES SCREEN'S OWN SMALL PAGE (2026-08-30) — separate from
   * `agreements` above, which the policies section still needs as the
   * fullest page it can get (SKU delete-refusal, per-offer rollups) and must
   * NOT be starved down to a handful of rows. This is a second, narrow
   * request for the top 20 (server default status active+ending, ordered by
   * remaining balance) plus whole-book totals; the FIVE attention rows are
   * ranked out of these 20 client-side.
   */
  const [financingAttention, setFinancingAttention] = useState<FinanceAgreementRow[]>([]);
  const [financingAttentionTotals, setFinancingAttentionTotals] = useState<AgreementRollupLike | undefined>();
  /**
   * Whether the attention-page fetch above failed. A stat tile states a
   * money figure as fact — on failure this suppresses the tiles/rows instead
   * of silently rendering them as zero (Ruling 13, Important 1).
   */
  const [financingAttentionFailed, setFinancingAttentionFailed] = useState(false);
  const [openDamage, setOpenDamage] = useState<DamageRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [damageTarget, setDamageTarget] = useState<{ unitId: string; costCents: number | undefined } | null>(null);
  /*
    EDITING A DEVICE IS A ROUTE NOW (2026-08-30), not a modal owned by this
    shell — see `DeviceEditorPage`. The form is long enough that a 92vh dialog
    made it scroll inside a scroll, and creating a device now ends with a
    question ("is it sold?") whose answer navigates somewhere.
  */
  const openDeviceEditor = useCallback(
    (id: string) => navigate(id ? `/inventory/stock/${encodeURIComponent(id)}/edit` : '/inventory/stock/new'),
    [navigate],
  );
  const openDeviceDuplicate = useCallback(
    (id: string) => navigate(`/inventory/stock/new?duplicate=${encodeURIComponent(id)}`),
    [navigate],
  );

  /**
   * Pages GET /financing to exhaustion across EVERY status (`all` — including
   * closed/cancelled), 100 at a time, so `agreements` is genuinely the whole
   * book the delete-refusal gate promises. Terminates on `nextToken` ONLY —
   * never on page length or `count < limit`, because DynamoDB applies its
   * filter after reading `Limit`, so a filtered page may legitimately come
   * back short. Capped at 200 pages (20,000 rows) so a server bug can't hang
   * this fetch forever; hitting the cap is logged and reported as incomplete
   * rather than silently truncated.
   */
  const fetchWholeAgreementBook = useCallback(async (): Promise<{
    rows: FinanceAgreementRow[]; generatedAt: number | null; complete: boolean;
  }> => {
    const rows: FinanceAgreementRow[] = [];
    let nextToken: string | undefined;
    let generatedAt: number | null = null;
    const MAX_PAGES = 200;
    for (let page = 0; page < MAX_PAGES; page++) {
      const res = await billingApi.listFinancing({ status: 'all', limit: 100, nextToken });
      rows.push(...(res.agreements || []));
      generatedAt = res.generatedAt || generatedAt;
      nextToken = res.nextToken || undefined;
      if (!nextToken) return { rows, generatedAt, complete: true };
    }
    // eslint-disable-next-line no-console
    console.error(
      `[InventoryHubPage] agreement book exceeded the ${MAX_PAGES}-page cap (${rows.length} rows) — ` +
      'treating as incomplete; the SKU delete gate will refuse rather than trust this list.',
    );
    return { rows, generatedAt, complete: false };
  }, []);

  /**
   * Runs the exhaustive book fetch on ITS OWN — never inside the `load()`
   * `Promise.all` below. Up to 200 sequential round-trips must never delay
   * the stock list rendering; the header comment on `load()` promises a
   * FAILURE there won't blank the stock list, and unbounded latency would
   * quietly break that same promise.
   *
   * `agreementsComplete` is reset to `false` SYNCHRONOUSLY at the top of
   * every run, before the first `await` — not just seeded `false` by
   * `useState`. Decoupling this from `load()`'s blocking `loading` flag
   * means the delete gate is reachable while a re-fetch (e.g. the `onChanged`
   * right after a retire/delete) is in flight; without this reset,
   * `agreementsComplete` would keep its stale prior `true` value for that
   * whole window and the gate would wrongly permit a delete. It is set back
   * to `true` only once a complete, uninterrupted pass has landed — never
   * optimistically. `agreements` itself is left alone on a re-run so the
   * count/line list don't flicker to empty; only the completeness CLAIM
   * has to go false immediately.
   *
   * Guarded by `agreementBookSeq` (same request-sequence pattern as
   * `FinanceBookPage`'s `requestSeq`): two overlapping runs — a fast reload,
   * or a retire followed quickly by another — could otherwise resolve out of
   * order and let a stale snapshot win.
   */
  const loadAgreementBook = useCallback(async () => {
    const seq = ++agreementBookSeq.current;
    setAgreementsComplete(false);
    try {
      const r = await fetchWholeAgreementBook();
      if (seq !== agreementBookSeq.current) return; // superseded — drop this response
      setFinancingError(null);
      setAgreements(r.rows);
      setFinancingGeneratedAt(r.generatedAt);
      setAgreementsComplete(r.complete);
    } catch (e: any) {
      if (seq !== agreementBookSeq.current) return; // superseded — don't surface a stale error
      setFinancingError(e?.message || 'Failed to load financing agreements');
      setAgreements([]);
      setFinancingGeneratedAt(null);
      setAgreementsComplete(false);
    }
  }, [fetchWholeAgreementBook]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    // Fired, not awaited — see `loadAgreementBook`'s own comment for why.
    loadAgreementBook();
    try {
      const [m, u, v, s, d, b, finAttn] = await Promise.all([
        hardwareApi.listModels(),
        hardwareApi.listUnits(),
        inventoryApi.listVendors().catch(() => ({ items: [] as VendorRow[], count: 0 })),
        billingApi.listSkus().catch(() => ({ items: [] as BillingSkuRow[] })),
        // ONE fetch of every open damage report, mapped onto units client-side
        // (StockTab's ⚠ badge) — never a per-unit damage scan on a list.
        inventoryApi.listDamage({ status: 'open' }).catch(() => ({ items: [] as DamageRow[], count: 0 })),
        // Bundles feed the policy cards' device names only — best-effort, the
        // same posture the standalone Financing page took.
        hardwareApi.listBundles().catch(() => ({ items: [] as DeviceBundleRow[] })),
        // The Devices screen's own attention page: server default status
        // (active+ending, ordered by remaining balance), 20 rows — enough
        // that the urgent tiers ranked client-side are reachable, never the
        // whole book, which now lives at /inventory/finance-book. `totals`
        // is whole-book regardless of how many rows come back. A tile states
        // a money figure as fact, so failure is NOT best-effort-to-zero here
        // (unlike vendors/bundles/damage above): it is logged and flagged so
        // the screen shows "couldn't load" instead of a false $0.
        billingApi.listFinancing({ limit: 20 })
          .then((r) => ({ agreements: r.agreements, totals: r.totals, failed: false }))
          .catch((e: any) => {
            // eslint-disable-next-line no-console
            console.error('[InventoryHubPage] failed to load the devices-screen attention page', e);
            return { agreements: [] as FinanceAgreementRow[], totals: undefined as AgreementRollupLike | undefined, failed: true };
          }),
      ]);
      setModels(m.items || []);
      setInvalidModels(m.invalid || []);
      setAllUnits(u.items || []);
      setVendors(v.items || []);
      const deviceSkus = (s.items || []).filter((x) => x.type === 'device_finance');
      setAllSkus(deviceSkus);
      setSkus(deviceSkus.filter((x) => x.status === 'active'));
      setBundles(b.items || []);
      setFinancingAttention(finAttn.agreements || []);
      setFinancingAttentionTotals(finAttn.totals);
      setFinancingAttentionFailed(finAttn.failed === true);
      setOpenDamage(d.items || []);
    } catch (e: any) {
      setError(e?.message || 'Failed to load inventory');
    } finally {
      setLoading(false);
    }
  }, [loadAgreementBook]);

  useEffect(() => { load(); }, [load]);

  const openDamageByUnit = useMemo(() => {
    const map = new Map<string, DamageRow[]>();
    for (const d of openDamage) {
      if (!map.has(d.unitId)) map.set(d.unitId, []);
      map.get(d.unitId)!.push(d);
    }
    return map;
  }, [openDamage]);

  // Mid-redirect from a legacy mount — render nothing for the one frame
  // before the effect above lands on the section's own route.
  if (legacyTarget) return null;

  const section = sectionFor(tab);

  return (
    <InventoryPage
      icon={tab === 'stock' ? Monitor : Boxes}
      title={section.label}
      subtitle={section.subtitle}
      actions={tab !== 'grn' ? (
        <button
          onClick={() => setTab('grn')}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-medium min-h-[44px]"
          style={{ background: '#DC143C' }}
        >
          <PackagePlus size={16} /> Receive stock
        </button>
      ) : undefined}
    >
      {tab === 'stock' && (
        <StockTab
          models={models}
          invalidModels={invalidModels}
          allUnits={allUnits}
          skus={skus}
          loading={loading}
          error={error}
          onReceiveStock={() => setTab('grn')}
          onDamage={(unitId, costCents) => setDamageTarget({ unitId, costCents })}
          reload={load}
          openDamageByUnit={openDamageByUnit}
          onEditModel={openDeviceEditor}
          onDuplicateModel={openDeviceDuplicate}
          allSkus={allSkus}
          bundles={bundles}
          agreements={agreements}
          financingGeneratedAt={financingGeneratedAt}
          financingError={financingError}
          agreementsComplete={agreementsComplete}
          financingAttention={financingAttention}
          financingAttentionTotals={financingAttentionTotals}
          financingAttentionFailed={financingAttentionFailed}
        />
      )}
      {tab === 'grn' && (
        <GrnPanel
          models={models}
          vendors={vendors}
          units={allUnits}
          skus={skus}
          onEditModel={openDeviceEditor}
          onDuplicateModel={openDeviceDuplicate}
          reloadModels={load}
        />
      )}
      {tab === 'vendors' && <VendorsPanel />}
      {tab === 'adjustments' && <AdjustmentsTab models={models} reload={load} />}
      {tab === 'damage' && <DamagePanel models={models} units={allUnits} />}
      {tab === 'provisioning' && <ProvisioningTemplateEditor />}
      {tab === 'brands' && <BrandsPanel models={models} />}

      {damageTarget && (
        <DamageWizard
          unitId={damageTarget.unitId}
          unitCostCents={damageTarget.costCents}
          onClose={() => setDamageTarget(null)}
          onSaved={load}
        />
      )}

    </InventoryPage>
  );
};

export default InventoryHubPage;
